import React, { useEffect, useState, useRef } from "react";
import { Header } from "./Header";
import { useI18n } from "../context/I18nContext";
import { api, useAuth } from "../context/AuthContext";
import { money, dateFmt } from "../lib/format";
import { ActivityFeed, ActivityPanel } from "./ActivityFeed";
import { ClockCounterClockwise as ActIcon } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Checkbox } from "./ui/checkbox";
import { DashboardNav } from "./DashboardNav";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Users, PiggyBank, ChartLineUp, HandCoins, Percent, MagnifyingGlass, Plus, ArrowsLeftRight, ShieldCheck, UserPlus, Trash, PencilSimple, ClockCounterClockwise, Prohibit, CheckCircle, FilePdf, Coins, Vault, CalendarCheck, EnvelopeSimple, ArrowsClockwise, ChartBar, LinkSimple, Image, FileXls, UploadSimple, CaretUp, CaretDown, ShareNetwork, Copy, Bank, PaperPlaneTilt, Receipt, Truck, Trophy, Medal, Ticket, IdentificationCard, Cake, Question, Star, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ReferralPanel } from "./ReferralPanel";
import { SavingsAccountsPanel } from "./SavingsAccountsPanel";
import { LoansPanel } from "./LoansPanel";
import { CapitalPanel } from "./CapitalPanel";
import { PortfolioPanel } from "./PortfolioPanel";
import { CollectionsPanel } from "./CollectionsPanel";
import { TransfersAdminPanel } from "./TransfersAdminPanel";
import { RemittancesPanel } from "./RemittancesPanel";
import { BranchRankingPanel } from "./BranchRankingPanel";
import { RafflePanel } from "./RaffleTickets";
import { CalendarPanel } from "./CalendarPanel";
import { IdDocumentsPanel } from "./IdDocumentsPanel";
import { NotificationLogPanel } from "./NotificationLogPanel";
import { provinceColor } from "../lib/provinces";

export const ALL_PERMS = ["view_clients", "manage_accounts", "manage_transactions", "manage_loans", "manage_rates", "manage_staff", "view_audit", "manage_content"];

export const PERM_ICONS = { manage_staff: ShieldCheck };

async function downloadStatement(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type || "application/octet-stream" }));
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
}

export function AuditPanel() {
  const { t, lang } = useI18n();
  const [entries, setEntries] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ actor: "", action: "all", date_from: "", date_to: "" });

  const fetchEntries = () => {
    setLoading(true);
    const params = {};
    if (f.actor) params.actor = f.actor;
    if (f.action && f.action !== "all") params.action = f.action;
    if (f.date_from) params.date_from = f.date_from;
    if (f.date_to) params.date_to = f.date_to;
    api.get("/admin/audit", { params }).then((r) => setEntries(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/admin/audit/actions").then((r) => setActions(r.data)).catch(() => {});
  }, []);
  useEffect(() => { fetchEntries(); }, [f]); // eslint-disable-line

  const clear = () => setF({ actor: "", action: "all", date_from: "", date_to: "" });

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="audit-panel">
      <div className="flex items-center gap-2 mb-4">
        <ClockCounterClockwise size={22} weight="duotone" className="text-emerald-700" />
        <h3 className="font-display font-bold text-emerald-900 text-xl">{t("audit_log")}</h3>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <Input data-testid="audit-filter-actor" placeholder={t("filter_actor")} value={f.actor} onChange={(e) => setF({ ...f, actor: e.target.value })} />
        <Select value={f.action} onValueChange={(v) => setF({ ...f, action: v })}>
          <SelectTrigger data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("all_actions")}</SelectItem>
            {actions.map((a) => (
              <SelectItem key={a} value={a}>{t("act_" + a) !== "act_" + a ? t("act_" + a) : a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input data-testid="audit-filter-from" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} />
        <Input data-testid="audit-filter-to" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} />
        <Button data-testid="audit-filter-clear" variant="outline" onClick={clear} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">{t("clear_filters")}</Button>
      </div>

      {loading ? <p className="text-slate-500 py-6">{t("loading")}</p> : entries.length === 0 ? (
        <p className="text-slate-500 text-sm py-4">{t("no_audit")}</p>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="audit-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("audit_date")}</TableHead>
                <TableHead>{t("audit_actor")}</TableHead>
                <TableHead>{t("audit_action")}</TableHead>
                <TableHead>{t("audit_detail")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.id} data-testid="audit-row">
                  <TableCell className="text-xs text-slate-400 whitespace-nowrap">{dateFmt(e.created_at, lang)}</TableCell>
                  <TableCell className="text-sm font-semibold text-emerald-900">{e.actor_name}</TableCell>
                  <TableCell><span className="text-xs bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded font-semibold">{t("act_" + e.action) !== "act_" + e.action ? t("act_" + e.action) : e.action}</span></TableCell>
                  <TableCell className="text-sm text-slate-600">{e.detail}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export function OverviewPanel({ clients }) {
  const { t, lang } = useI18n();
  const [deposits, setDeposits] = useState([]);
  useEffect(() => {
    api.get("/admin/term-deposits").then((r) => setDeposits(r.data)).catch(() => {});
  }, []);

  const regular = [];
  clients.forEach((c) => {
    c.accounts.forEach((a) => regular.push({ ...a, client: c.user.name }));
  });

  const totals = {};
  const bump = (label, cur, amt) => {
    totals[label] = totals[label] || {};
    totals[label][cur] = (totals[label][cur] || 0) + amt;
  };
  regular.forEach((a) => {
    if (a.type === "savings") bump("savings", a.currency, a.balance);
    else if (a.type === "shares") bump("shares", a.currency, a.balance);
    else if (a.type === "loan") bump("loans", a.currency, a.remaining_balance);
  });
  deposits.forEach((d) => bump("term", d.currency, d.principal));

  const byCurrency = { DOP: 0, USD: 0 };
  regular.forEach((a) => { if (a.type !== "loan") byCurrency[a.currency] = (byCurrency[a.currency] || 0) + a.balance; });
  deposits.forEach((d) => { byCurrency[d.currency] = (byCurrency[d.currency] || 0) + d.principal; });

  const fmtT = (obj) => obj ? Object.keys(obj).map((k) => money(obj[k], k)).join(" · ") : "—";
  const typeLabel = (ty) => ty === "loan" ? t("loan") : t(ty);

  return (
    <div className="space-y-6" data-testid="overview-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-display font-bold text-emerald-900 text-xl">{t("all_accounts_breakdown")}</h3>
        <div className="flex gap-2">
          <Button
            data-testid="download-overview-btn"
            size="sm" variant="outline"
            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"
            onClick={() => downloadStatement("/admin/overview/pdf", "resumen_cooperativa.pdf").catch(() => toast.error("Error"))}
          >
            <FilePdf size={16} weight="bold" /> {t("download_overview")}
          </Button>
          <Button
            data-testid="download-overview-excel-btn"
            size="sm" variant="outline"
            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"
            onClick={() => downloadStatement("/admin/overview/excel", "resumen_cooperativa.xlsx").catch(() => toast.error("Error"))}
          >
            <FileXls size={16} weight="bold" /> {t("download_excel")}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="overview-by-currency">
        <StatCard icon={Coins} label={t("total_dop")} value={money(byCurrency.DOP || 0, "DOP")} />
        <StatCard icon={Coins} label={t("total_usd")} value={money(byCurrency.USD || 0, "USD")} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={PiggyBank} label={t("total_savings")} value={fmtT(totals.savings) || "—"} />
        <StatCard icon={ChartLineUp} label={t("total_shares")} value={fmtT(totals.shares) || "—"} />
        <StatCard icon={HandCoins} label={t("loan")} value={fmtT(totals.loans) || "—"} />
        <StatCard icon={Vault} label={t("term_deposits")} value={fmtT(totals.term) || "—"} />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h4 className="font-display font-bold text-emerald-900 mb-3">{t("regular_accounts")}</h4>
        <div className="overflow-x-auto">
          <Table data-testid="overview-regular-table">
            <TableHeader><TableRow>
              <TableHead>{t("client")}</TableHead><TableHead>{t("type")}</TableHead>
              <TableHead>{t("current_balance")}</TableHead><TableHead>{t("accrued_earnings")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {regular.length === 0 ? <TableRow><TableCell colSpan={4} className="text-slate-400 text-sm">—</TableCell></TableRow> :
                regular.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-semibold text-emerald-900">{a.client}</TableCell>
                    <TableCell><span className="text-xs bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded font-semibold">{typeLabel(a.type)}</span></TableCell>
                    <TableCell className="font-mono-num">{a.type === "loan" ? money(a.remaining_balance, a.currency) : money(a.balance, a.currency)}</TableCell>
                    <TableCell className="font-mono-num text-emerald-700">{a.type === "loan" ? "—" : money(a.earnings, a.currency)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h4 className="font-display font-bold text-emerald-900 mb-3">{t("term_deposits")}</h4>
        <div className="overflow-x-auto">
          <Table data-testid="overview-term-table">
            <TableHeader><TableRow>
              <TableHead>{t("account_number")}</TableHead><TableHead>{t("holder")}</TableHead>
              <TableHead>{t("deposit_amount")}</TableHead><TableHead>{t("current_value")}</TableHead><TableHead>{t("maturity")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {deposits.length === 0 ? <TableRow><TableCell colSpan={5} className="text-slate-400 text-sm">—</TableCell></TableRow> :
                deposits.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-mono-num text-emerald-800 font-semibold">{d.account_number}</TableCell>
                    <TableCell>{d.full_name}</TableCell>
                    <TableCell className="font-mono-num">{money(d.principal, d.currency)}</TableCell>
                    <TableCell className="font-mono-num font-bold">{money(d.current_value, d.currency)}</TableCell>
                    <TableCell>{dateFmt(d.end_date, lang)} <span className={`ml-1 text-[11px] px-1.5 py-0.5 rounded ${d.matured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{d.matured ? t("matured") : t("active_term")}</span></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function CollateralDialog({ deposit, onDone }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [on, setOn] = useState(!!deposit.collateral);
  const [note, setNote] = useState(deposit.collateral_note || "");
  const [internal, setInternal] = useState(deposit.collateral_internal_note || "");
  useEffect(() => { if (open) { setOn(!!deposit.collateral); setNote(deposit.collateral_note || ""); setInternal(deposit.collateral_internal_note || ""); } }, [open]); // eslint-disable-line
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/term-deposits/${deposit.id}/collateral`, { collateral: on, note, internal_note: internal });
      toast.success(on ? "Acción marcada en garantía" : "Garantía retirada");
      setOpen(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`collateral-term-${deposit.id}`} size="sm" variant="outline" className={`${deposit.collateral ? "border-amber-400 text-amber-700 bg-amber-50" : "border-emerald-300 text-emerald-800"} hover:bg-amber-50`} title="Garantía"><ShieldCheck size={15} weight="bold" /></Button>
      </DialogTrigger>
      <DialogContent data-testid="collateral-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">Garantía — {deposit.account_number}</DialogTitle>
          <DialogDescription className="sr-only">Garantía de la Acción</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex items-start gap-2.5 cursor-pointer text-sm text-slate-700" data-testid="collateral-toggle-label">
            <input type="checkbox" data-testid="collateral-toggle" checked={on} onChange={(e) => setOn(e.target.checked)} className="h-4 w-4 mt-0.5 accent-amber-600" />
            <span>Marcar esta Acción como <b>“En garantía”</b> (bloquea la liquidación anticipada mientras esté activa).</span>
          </label>
          <div>
            <Label>Nota visible para el socio</Label>
            <Input data-testid="collateral-note" className="mt-1 h-11" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej.: Usada como garantía del préstamo PR-1234" />
          </div>
          <div>
            <Label>Nota interna (solo admin)</Label>
            <Input data-testid="collateral-internal-note" className="mt-1 h-11" value={internal} onChange={(e) => setInternal(e.target.value)} placeholder="Notas para el comité (no visibles al socio)" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="collateral-save" onClick={save} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900">{busy ? t("loading") : t("save_changes")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function TermDepositsPanel() {  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = () => {
    api.get("/admin/term-deposits").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.delete(`/admin/term-deposits/${id}`); toast.success(t("term_deposit_deleted")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const downloadCertificate = async (d) => {
    try {
      const res = await api.get(`/admin/term-deposits/${d.id}/certificate`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `certificado_${d.account_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
  };

  const notify = async (id) => {
    try { const { data } = await api.post(`/admin/term-deposits/${id}/notify`); toast.success(data.message || t("notice_sent")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const q = search.toLowerCase();
  const filtered = items.filter((d) => d.account_number?.toLowerCase().includes(q) || d.full_name?.toLowerCase().includes(q) || (d.email || "").toLowerCase().includes(q) || (d.phone || "").toLowerCase().includes(q));

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="term-deposits-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Vault size={22} weight="duotone" className="text-emerald-700" />
          <h3 className="font-display font-bold text-emerald-900 text-xl">{t("term_deposits")}</h3>
        </div>
        <TermDepositDialog onDone={load} />
      </div>
      <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="td-notice-admin">
        <ClockCounterClockwise size={20} weight="duotone" className="text-amber-700 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800 leading-relaxed">{t("td_notice")}</p>
      </div>
      <div className="relative mb-4 max-w-sm">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="term-search" placeholder={t("search_deposit")} className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      {loading ? <p className="text-slate-500 py-6">{t("loading")}</p> : filtered.length === 0 ? (
        <p className="text-slate-500 text-sm py-4">{t("no_term_deposits")}</p>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="term-deposits-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("account_number")}</TableHead>
                <TableHead>{t("holder")}</TableHead>
                <TableHead>{t("deposit_amount")}</TableHead>
                <TableHead>{t("accrued_interest")}</TableHead>
                <TableHead>{t("current_value")}</TableHead>
                <TableHead>{t("maturity")}</TableHead>
                <TableHead className="text-right">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((d) => (
                <TableRow key={d.id} data-testid={`term-row-${d.id}`}>
                  <TableCell className="font-mono-num text-emerald-800 font-semibold">{d.account_number}<div className="text-[11px] text-slate-400 font-normal">{t("folio")}: {d.folio}</div></TableCell>
                  <TableCell>
                    <div className="font-semibold text-emerald-900">{d.full_name}</div>
                    <div className="text-xs text-slate-400">{d.email || d.phone}</div>
                    {d.user_id && <span className="inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" data-testid={`linked-badge-${d.id}`}>{t("linked")}</span>}
                    {d.user_id && d.linked_by && <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`linked-info-${d.id}`}>{t("linked_by")}: {d.linked_by}{d.linked_at ? ` · ${dateFmt(d.linked_at, lang)}` : ""}</div>}
                  </TableCell>
                  <TableCell className="font-mono-num">{money(d.principal, d.currency)} <span className="text-xs text-slate-400">· {d.annual_rate}%</span></TableCell>
                  <TableCell className="font-mono-num text-emerald-700">{money(d.accrued_interest, d.currency)}<div className="text-xs text-slate-400">{d.days_elapsed}/{d.term_days} {t("days_elapsed").toLowerCase()}</div></TableCell>
                  <TableCell className="font-mono-num font-bold">{money(d.current_value, d.currency)}</TableCell>
                  <TableCell>
                    <div className="text-sm">{dateFmt(d.end_date, lang)}</div>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${d.matured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{d.matured ? t("matured") : t("active_term")}</span>
                    {d.collateral && <span className="block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" data-testid={`collateral-badge-${d.id}`} title={d.collateral_note || "En garantía"}>En garantía</span>}
                    {d.last_notified_at && <div className="text-[10px] text-slate-400 mt-1">{t("last_notice")}: {dateFmt(d.last_notified_at, lang)}</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1.5 justify-end">
                      <Button data-testid={`cert-term-${d.id}`} size="sm" variant="outline" onClick={() => downloadCertificate(d)} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50" title={t("certificate")}><FilePdf size={15} weight="bold" /></Button>
                      <Button data-testid={`notify-term-${d.id}`} size="sm" variant="outline" onClick={() => notify(d.id)} disabled={!d.email} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 disabled:opacity-40" title={t("send_notice")}><EnvelopeSimple size={15} weight="bold" /></Button>
                      <LinkClientDialog deposit={d} onDone={load} />
                      <TermDepositDialog editing={d} onDone={load} />
                      <RenewDialog deposit={d} onDone={load} />
                      <CollateralDialog deposit={d} onDone={load} />
                      {!d.matured && <LiquidateDialog deposit={d} onDone={load} />}
                      <Button data-testid={`delete-term-${d.id}`} size="sm" variant="outline" onClick={() => remove(d.id)} className="border-red-200 text-red-700 hover:bg-red-50" title={t("delete")}><Trash size={15} weight="bold" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export function LinkClientDialog({ deposit, onDone }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [sel, setSel] = useState(deposit.user_id || "none");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setSel(deposit.user_id || "none");
      api.get("/admin/clients").then((r) => setClients(r.data.map((c) => c.user))).catch(() => {});
    }
  }, [open]); // eslint-disable-line

  const submit = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/term-deposits/${deposit.id}/link`, { user_id: sel === "none" ? null : sel });
      toast.success(t("linked"));
      setOpen(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`link-term-${deposit.id}`} size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50" title={t("link_client")}><LinkSimple size={15} weight="bold" /></Button>
      </DialogTrigger>
      <DialogContent data-testid="link-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">{t("link_client")} · {deposit.account_number}</DialogTitle>
          <DialogDescription className="sr-only">{t("link_client")}</DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-emerald-900 font-semibold">{t("select_client")}</Label>
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger data-testid="link-client-select" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("no_link")}</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name} — {c.email || c.phone}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button data-testid="link-confirm" onClick={submit} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900">{t("confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RenewDialog({ deposit, onDone }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [months, setMonths] = useState("");
  const [capitalize, setCapitalize] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/term-deposits/${deposit.id}/renew`, { months: months ? Number(months) : undefined, capitalize });
      toast.success(t("renewed_ok"));
      setOpen(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`renew-term-${deposit.id}`} size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" title={t("renew")}><ArrowsClockwise size={15} weight="bold" /></Button>
      </DialogTrigger>
      <DialogContent data-testid="renew-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">{t("renew_deposit")} · {deposit.account_number}</DialogTitle>
          <DialogDescription className="sr-only">{t("renew_deposit")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-emerald-900 font-semibold">{t("renew_months")}</Label>
            <Input data-testid="renew-months" type="number" placeholder={`${deposit.term_days} ${t("days_elapsed").toLowerCase()}`} className="mt-1.5 h-11 font-mono-num" value={months} onChange={(e) => setMonths(e.target.value)} />
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox data-testid="renew-capitalize" checked={capitalize} onCheckedChange={(v) => setCapitalize(!!v)} />
            <span className="text-sm text-emerald-900">{t("capitalize_interest")}</span>
          </label>
          <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-sm text-emerald-800 font-mono-num">
            {t("current_value")}: <b>{money(deposit.maturity_value, deposit.currency)}</b>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="renew-confirm" onClick={submit} disabled={busy} className="bg-amber-600 hover:bg-amber-700">{t("confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TermDepositDialog({ onDone, editing }) {
  const { t } = useI18n();
  const today = new Date().toISOString().slice(0, 10);
  const nextYear = new Date(Date.now() + 365 * 864e5).toISOString().slice(0, 10);
  const defaults = { first_name: "", last_name: "", address: "", phone: "", email: "", principal: "", currency: "DOP", annual_rate: "6", start_date: today, end_date: nextYear };
  const init = editing ? {
    first_name: editing.first_name || "", last_name: editing.last_name || "", address: editing.address || "", phone: editing.phone || "", email: editing.email || "",
    principal: String(editing.principal || ""), currency: editing.currency || "DOP", annual_rate: String(editing.annual_rate || ""),
    start_date: editing.start_date, end_date: editing.end_date,
  } : defaults;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(init);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  // live preview of accrued/projected interest
  const preview = (() => {
    const p = Number(f.principal) || 0;
    const r = Number(f.annual_rate) || 0;
    try {
      const s = new Date(f.start_date), en = new Date(f.end_date);
      const termDays = Math.max(1, Math.round((en - s) / 864e5));
      const proj = p * (r / 100) * termDays / 365;
      return { termDays, proj };
    } catch { return { termDays: 0, proj: 0 }; }
  })();

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        first_name: f.first_name, last_name: f.last_name, address: f.address, phone: f.phone, email: f.email,
        principal: Number(f.principal), currency: f.currency, annual_rate: Number(f.annual_rate),
        start_date: f.start_date, end_date: f.end_date,
      };
      if (editing) {
        await api.put(`/admin/term-deposits/${editing.id}`, payload);
        toast.success(t("act_term_deposit_update"));
      } else {
        await api.post("/admin/term-deposits", payload);
        toast.success(t("term_deposit_created"));
        setF(defaults);
      }
      setOpen(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing ? (
          <Button data-testid={`edit-term-${editing.id}`} size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50" title={t("edit")}><PencilSimple size={15} weight="bold" /></Button>
        ) : (
          <Button data-testid="new-term-deposit-btn" className="bg-emerald-800 hover:bg-emerald-900 gap-1.5"><Plus size={18} weight="bold" /> {t("new_term_deposit")}</Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="term-deposit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">{editing ? t("edit_term_deposit") : t("new_term_deposit")}</DialogTitle>
          <DialogDescription className="sr-only">{editing ? t("edit_term_deposit") : t("new_term_deposit")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-emerald-900 font-semibold">{t("first_name")}</Label><Input data-testid="td-first-name" className="mt-1.5 h-11" value={f.first_name} onChange={set("first_name")} /></div>
            <div><Label className="text-emerald-900 font-semibold">{t("last_name")}</Label><Input data-testid="td-last-name" className="mt-1.5 h-11" value={f.last_name} onChange={set("last_name")} /></div>
          </div>
          <div><Label className="text-emerald-900 font-semibold">{t("address")}</Label><Input data-testid="td-address" className="mt-1.5 h-11" value={f.address} onChange={set("address")} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-emerald-900 font-semibold">{t("phone")}</Label><Input data-testid="td-phone" className="mt-1.5 h-11" value={f.phone} onChange={set("phone")} /></div>
            <div><Label className="text-emerald-900 font-semibold">{t("email")}</Label><Input data-testid="td-email" type="email" className="mt-1.5 h-11" value={f.email} onChange={set("email")} /></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><Label className="text-emerald-900 font-semibold">{t("deposit_amount")}</Label><Input data-testid="td-principal" type="number" className="mt-1.5 h-11 font-mono-num" value={f.principal} onChange={set("principal")} /></div>
            <div>
              <Label className="text-emerald-900 font-semibold">{t("currency")}</Label>
              <Select value={f.currency} onValueChange={(v) => setF({ ...f, currency: v })}>
                <SelectTrigger data-testid="td-currency" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="DOP">RD$</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-emerald-900 font-semibold">{t("rate")}</Label><Input data-testid="td-rate" type="number" step="0.1" className="mt-1.5 h-11 font-mono-num" value={f.annual_rate} onChange={set("annual_rate")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-emerald-900 font-semibold">{t("start_date")}</Label><Input data-testid="td-start" type="date" className="mt-1.5 h-11" value={f.start_date} onChange={set("start_date")} /></div>
            <div><Label className="text-emerald-900 font-semibold">{t("end_date")}</Label><Input data-testid="td-end" type="date" className="mt-1.5 h-11" value={f.end_date} onChange={set("end_date")} /></div>
          </div>
          <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-sm flex items-center gap-2 text-emerald-800" data-testid="td-preview">
            <CalendarCheck size={18} weight="duotone" />
            <span>{t("projected_interest")}: <b className="font-mono-num">{money(preview.proj, f.currency)}</b> · {preview.termDays} {t("days_elapsed").toLowerCase()}</span>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="td-submit" onClick={submit} disabled={busy || !f.first_name || !f.last_name || !f.principal} className="bg-emerald-800 hover:bg-emerald-900">{t("confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function StaffPanel({ myPermissions }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await api.delete(`/admin/staff/${id}`);
      toast.success(t("employee_deleted"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const toggleSuspend = async (s) => {
    try {
      await api.put(`/admin/staff/${s.id}/suspend`, { suspended: !s.suspended });
      toast.success(!s.suspended ? t("act_staff_suspend") : t("act_staff_reactivate"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="staff-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={22} weight="duotone" className="text-emerald-700" />
          <h3 className="font-display font-bold text-emerald-900 text-xl">{t("employees")}</h3>
        </div>
        <StaffFormDialog myPermissions={myPermissions} onDone={load} />
      </div>
      {loading ? <p className="text-slate-500 py-6">{t("loading")}</p> : (
        <div className="overflow-x-auto">
          <Table data-testid="staff-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("client")}</TableHead>
                <TableHead>{t("role")}</TableHead>
                <TableHead>{t("permissions")}</TableHead>
                <TableHead className="text-right">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {staff.map((s) => (
                <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                  <TableCell>
                    <div className="font-semibold text-emerald-900">{s.name}</div>
                    <div className="text-xs text-slate-400">{s.email || s.phone}</div>
                    {s.role === "staff" && s.branch ? (
                      <div className="text-[11px] text-emerald-700 font-medium mt-0.5">{t("branch_name")}: {s.branch}</div>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full w-fit ${s.role === "admin" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                        {s.role === "admin" ? t("super_admin") : t("staff")}
                      </span>
                      {s.role === "staff" && (
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded w-fit ${s.suspended ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`}>
                          {s.suspended ? t("suspended") : t("active_label")}
                        </span>
                      )}
                      <span data-testid={`twofa-status-${s.id}`} className={`text-[11px] font-semibold px-2 py-0.5 rounded w-fit inline-flex items-center gap-1 ${s.twofa_enabled ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        <ShieldCheck size={12} weight="bold" /> {s.twofa_enabled ? t("twofa_yes") : t("twofa_no")}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-md">
                      {s.role === "admin" ? (
                        <span className="text-xs text-slate-500">{t("perm_manage_staff")} + …</span>
                      ) : s.permissions.length === 0 ? (
                        <span className="text-xs text-slate-300">—</span>
                      ) : s.permissions.map((p) => (
                        <span key={p} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{t("perm_" + p)}</span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {s.role === "staff" && s.id !== user?.id ? (
                      <div className="flex gap-2 justify-end">
                        <StaffFormDialog myPermissions={myPermissions} editing={s} onDone={load} />
                        <Button data-testid={`suspend-staff-${s.id}`} size="sm" variant="outline" onClick={() => toggleSuspend(s)} className={s.suspended ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-amber-300 text-amber-700 hover:bg-amber-50"} title={s.suspended ? t("reactivate") : t("suspend")}>
                          {s.suspended ? <CheckCircle size={15} weight="bold" /> : <Prohibit size={15} weight="bold" />}
                        </Button>
                        <Button data-testid={`delete-staff-${s.id}`} size="sm" variant="outline" onClick={() => remove(s.id)} className="border-red-200 text-red-700 hover:bg-red-50">
                          <Trash size={15} weight="bold" />
                        </Button>
                      </div>
                    ) : <span className="text-xs text-slate-300">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export function StaffFormDialog({ myPermissions, editing, onDone }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role_template: "none", branch: "" });
  const [perms, setPerms] = useState(editing ? editing.permissions : []);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isEdit = !!editing;

  useEffect(() => { if (open && editing) { setPerms(editing.permissions); setForm((f) => ({ ...f, branch: editing.branch || "" })); } }, [open, editing]);

  const togglePerm = (p) => setPerms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);

  const applyTemplate = (tmpl) => {
    setForm({ ...form, role_template: tmpl });
    const templates = {
      cajero: ["view_clients", "manage_transactions"],
      oficial_credito: ["view_clients", "manage_loans"],
      gerente: ["view_clients", "manage_accounts", "manage_transactions", "manage_loans", "manage_rates", "view_audit", "manage_content"],
    };
    if (tmpl !== "none") setPerms(templates[tmpl].filter((p) => myPermissions.includes(p)));
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        await api.put(`/admin/staff/${editing.id}/permissions`, { permissions: perms, branch: form.branch });
        toast.success(t("permissions_updated"));
      } else {
        await api.post("/admin/staff", {
          name: form.name,
          email: form.email || undefined,
          phone: form.phone || undefined,
          password: form.password,
          permissions: perms,
          branch: form.branch || undefined,
        });
        toast.success(t("employee_created"));
        setForm({ name: "", email: "", phone: "", password: "", role_template: "none", branch: "" });
        setPerms([]);
      }
      setOpen(false);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button data-testid={`edit-staff-${editing.id}`} size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">
            <PencilSimple size={15} weight="bold" />
          </Button>
        ) : (
          <Button data-testid="new-employee-btn" className="bg-emerald-800 hover:bg-emerald-900 gap-1.5">
            <UserPlus size={18} weight="bold" /> {t("new_employee")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="staff-dialog">
        <DialogHeader><DialogTitle className="font-display text-emerald-900">{isEdit ? t("edit_permissions") : t("create_employee")}</DialogTitle><DialogDescription className="sr-only">{t("permissions")}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {!isEdit && (
            <>
              <div>
                <Label className="text-emerald-900 font-semibold">{t("full_name")}</Label>
                <Input data-testid="staff-name-input" className="mt-1.5 h-11" value={form.name} onChange={set("name")} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-emerald-900 font-semibold">{t("email")}</Label>
                  <Input data-testid="staff-email-input" type="email" className="mt-1.5 h-11" value={form.email} onChange={set("email")} />
                </div>
                <div>
                  <Label className="text-emerald-900 font-semibold">{t("phone")}</Label>
                  <Input data-testid="staff-phone-input" className="mt-1.5 h-11" value={form.phone} onChange={set("phone")} />
                </div>
              </div>
              <div>
                <Label className="text-emerald-900 font-semibold">{t("password")}</Label>
                <Input data-testid="staff-password-input" type="password" className="mt-1.5 h-11" value={form.password} onChange={set("password")} />
              </div>
              <div>
                <Label className="text-emerald-900 font-semibold">{t("role_template")}</Label>
                <Select value={form.role_template} onValueChange={applyTemplate}>
                  <SelectTrigger data-testid="staff-template-select" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("none_template")}</SelectItem>
                    <SelectItem value="cajero">{t("tmpl_cajero")}</SelectItem>
                    <SelectItem value="oficial_credito">{t("tmpl_oficial_credito")}</SelectItem>
                    <SelectItem value="gerente">{t("tmpl_gerente")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <div>
            <Label className="text-emerald-900 font-semibold">{t("branch_optional")}</Label>
            <Input data-testid="staff-branch-input" className="mt-1.5 h-11" value={form.branch} onChange={set("branch")} placeholder={t("branch_name")} />
          </div>
          <div>
            <Label className="text-emerald-900 font-semibold mb-2 block">{t("permissions")}</Label>
            <div className="space-y-2 rounded-lg border border-gray-100 p-3">
              {ALL_PERMS.map((p) => {
                const grantable = myPermissions.includes(p);
                return (
                  <label key={p} className={`flex items-center gap-3 py-1 ${grantable ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`}>
                    <Checkbox
                      data-testid={`perm-${p}`}
                      checked={perms.includes(p)}
                      disabled={!grantable}
                      onCheckedChange={() => grantable && togglePerm(p)}
                    />
                    <span className="text-sm text-emerald-900">{t("perm_" + p)}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-1.5">{t("no_permission_hint")}</p>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="staff-submit-btn" onClick={submit} disabled={busy || (!isEdit && (!form.name || !form.password))} className="bg-emerald-800 hover:bg-emerald-900">
            {isEdit ? t("save_permissions") : t("create_employee")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const StatCard = ({ icon: Icon, label, value, testId }) => (
  <motion.div data-testid={testId} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
    <div className="flex items-center gap-2 text-emerald-700 mb-2">
      <Icon size={20} weight="duotone" />
      <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
    </div>
    <p className="font-mono-num text-2xl font-bold text-emerald-900">{value}</p>
  </motion.div>
);

export const SUMMARY_DAY_LABELS = ["D", "L", "M", "M", "J", "V", "S"];

export const SummaryTrendChart = ({ t, daily, maxDay }) => (
  <div className="mt-3 pt-3 border-t border-white/15" data-testid="collections-trend">
    <p className="text-[10px] uppercase tracking-wider text-emerald-200/70 mb-1.5">{t("last_7_days")}</p>
    <div className="flex items-end justify-between gap-1.5 h-12">
      {daily.map((d, i) => {
        const pct = Math.round(((d.total || 0) / maxDay) * 100);
        const isToday = i === daily.length - 1;
        const dow = SUMMARY_DAY_LABELS[new Date(d.date + "T00:00:00Z").getUTCDay()];
        return (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: RD$ ${Number(d.total).toLocaleString("en-US", { minimumFractionDigits: 2 })}`}>
            <div className="w-full flex items-end h-9">
              <div className={`w-full rounded-t transition-all ${isToday ? "bg-white" : "bg-emerald-300/70"}`} style={{ height: `${Math.max(pct, d.total > 0 ? 8 : 3)}%` }} />
            </div>
            <span className={`text-[9px] ${isToday ? "text-white font-bold" : "text-emerald-200/60"}`}>{dow}</span>
          </div>
        );
      })}
    </div>
  </div>
);

export const SummaryGoalBar = ({ t, goal, progress }) => (
  goal > 0 ? (
    <div className="mt-3 pt-3 border-t border-white/15" data-testid="collections-month-goal">
      <div className="flex justify-between text-[10px] text-emerald-200/80 mb-1">
        <span>{t("goal_lower")}: RD$ {Number(goal).toLocaleString("en-US")}</span>
        <span className="font-bold text-white">{progress}%</span>
      </div>
      <div className="h-2 w-full rounded-full bg-white/15 overflow-hidden">
        <div className="h-full rounded-full bg-white transition-all" style={{ width: `${progress}%` }} />
      </div>
    </div>
  ) : null
);

export const SummaryCard = ({ t, label, bucket, testId, onOpen, fmtBucket, countBucket, children }) => (
  <motion.button
    type="button"
    data-testid={testId}
    onClick={onOpen}
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className="text-left bg-gradient-to-br from-emerald-800 to-emerald-950 rounded-xl shadow-sm p-5 hover:from-emerald-700 hover:to-emerald-900 transition-colors"
  >
    <div className="flex items-center gap-2 text-emerald-200 mb-2">
      <Receipt size={20} weight="duotone" />
      <span className="text-xs uppercase tracking-wider font-semibold">{label}</span>
    </div>
    <p className="font-mono-num text-2xl font-bold text-white">{fmtBucket(bucket)}</p>
    <p className="text-xs text-emerald-200/80 mt-1">{countBucket(bucket)} {t("movements_lower")}</p>
    {children}
  </motion.button>
);

export const PendingRemittancesCard = ({ onOpen }) => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/remittances/pending-summary").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data || data.total_pending === 0) return null;
  const fmtTotals = (totals) => Object.entries(totals || {}).map(([c, v]) => money(v, c)).join(" · ");
  return (
    <motion.button
      type="button"
      onClick={onOpen}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="pending-remittances-card"
      className="mt-4 w-full text-left bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:border-emerald-200 transition-colors"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Truck size={20} weight="duotone" className="text-emerald-700" />
          <div>
            <h3 className="font-display font-bold text-emerald-900">{t("rm_pending_title")}</h3>
            <p className="text-xs text-slate-500">{t("rm_pending_sub")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data.total_overdue > 0 && (
            <span className="text-xs font-bold px-3 py-1 rounded-full bg-red-500 text-white" data-testid="pending-remittances-overdue" title={t("rm_overdue_hint")}>
              {data.total_overdue} {t("rm_overdue")} · +{data.overdue_days}d
            </span>
          )}
          <span className="text-sm font-bold px-3 py-1 rounded-full bg-red-100 text-red-600" data-testid="pending-remittances-total">{data.total_pending}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {data.provinces.map((p) => (
          <div key={p.province} className={`flex items-center justify-between rounded-lg border px-3 py-2 ${p.overdue_count > 0 ? "border-red-200 bg-red-50" : "border-gray-100"}`} data-testid={`pending-prov-${p.province}`}>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: provinceColor(p.province) }} /> {p.province}
              {p.overdue_count > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white" data-testid={`pending-overdue-${p.province}`}>{p.overdue_count} {t("rm_overdue")}</span>
              )}
            </span>
            <span className="text-right">
              <span className="font-mono-num font-bold text-emerald-800">{p.count}</span>
              <span className="block text-[10px] text-slate-400 font-mono-num">{fmtTotals(p.totals)}</span>
            </span>
          </div>
        ))}
      </div>
    </motion.button>
  );
};

export const MyBranchPositionCard = ({ branch, onOpen }) => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/branch-ranking").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  if (!data || !(data.ranking || []).length) return null;
  const idx = data.ranking.findIndex((r) => r.branch === branch);
  if (idx < 0) return null;
  const me = data.ranking[idx];
  const total = data.ranking.length;
  const pos = idx + 1;
  const above = idx > 0 ? data.ranking[idx - 1] : null;
  const gap = above ? Math.max(0, above.total - me.total) : 0;
  const medalColor = ["text-amber-300", "text-slate-200", "text-orange-300"][idx] || "text-emerald-200";
  return (
    <motion.button
      type="button"
      onClick={onOpen}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="my-branch-position-card"
      className="mt-6 w-full text-left rounded-2xl bg-gradient-to-br from-emerald-800 to-emerald-950 text-white p-5 flex items-center gap-5 hover:from-emerald-700 transition-colors"
    >
      <div className="shrink-0">
        {pos <= 3 ? <Medal size={46} weight="fill" className={medalColor} /> : <Trophy size={44} weight="duotone" className="text-emerald-200" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-emerald-200/70">{t("my_position_title")} · {branch}</p>
        <p className="font-display text-2xl font-bold mt-0.5" data-testid="my-position-text">
          {t("pos_you_are")} <span className="text-amber-300">#{pos}</span> {t("pos_of")} {total}
        </p>
        <p className="text-sm text-emerald-100/90 mt-1">
          <span className="font-mono-num font-semibold">{money(me.total, "DOP")}</span> {t("pos_collected")}
        </p>
      </div>
      <div className="hidden sm:block text-right max-w-[40%]">
        {above ? (
          <p className="text-xs text-emerald-100/80">
            {t("pos_gap_pre")} <span className="font-mono-num font-bold text-white">{money(gap, "DOP")}</span> {t("pos_gap_post")} <span className="font-semibold">{above.branch}</span>
          </p>
        ) : (
          <p className="text-sm font-semibold text-amber-300">{t("pos_leader")}</p>
        )}
      </div>
    </motion.button>
  );
};

export const CollectionsSummaryCards = ({ onOpen }) => {
  const { t } = useI18n();
  const [sum, setSum] = useState(null);
  const [kind, setKind] = useState("all");
  useEffect(() => {
    const params = kind !== "all" ? { kind } : {};
    api.get("/admin/collections-summary", { params }).then((r) => setSum(r.data)).catch(() => {});
  }, [kind]);

  const fmtBucket = (bucket) => {
    const cur = Object.keys(bucket || {});
    if (cur.length === 0) return money(0, "DOP");
    return cur.map((c) => money(bucket[c].total, c)).join(" · ");
  };
  const countBucket = (bucket) => Object.values(bucket || {}).reduce((a, b) => a + (b.count || 0), 0);

  const daily = sum?.daily || [];
  const maxDay = Math.max(1, ...daily.map((d) => d.total || 0));
  const goal = sum?.goal || 0;
  const progress = sum?.progress || 0;
  const kindTabs = [
    { k: "all", label: "kind_all" },
    { k: "loan_payment", label: "kind_loans" },
    { k: "savings_deposit", label: "kind_savings" },
    { k: "term_deposit", label: "kind_term" },
  ];

  return (
    <div className="mt-4 space-y-3" data-testid="collections-summary">
      <div className="flex flex-wrap gap-2" data-testid="summary-kind-tabs">
        {kindTabs.map((tab) => (
          <button key={tab.k} type="button" data-testid={`summary-kind-${tab.k}`} onClick={() => setKind(tab.k)}
            className={`text-xs px-3 py-1 rounded-full font-semibold transition-colors ${kind === tab.k ? "bg-emerald-800 text-white" : "bg-white text-slate-500 border border-slate-200 hover:bg-slate-50"}`}>
            {t(tab.label)}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SummaryCard t={t} label={t("collected_today")} bucket={sum?.today} testId="collections-today-card" onOpen={onOpen} fmtBucket={fmtBucket} countBucket={countBucket}>
          {daily.length > 0 && <SummaryTrendChart t={t} daily={daily} maxDay={maxDay} />}
          {(sum?.today_by_branch || []).length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/15" data-testid="collections-by-branch">
              <p className="text-[10px] uppercase tracking-wider text-emerald-200/70 mb-1.5">{t("collected_by_branch")}</p>
              <div className="space-y-1">
                {sum.today_by_branch.map((b) => (
                  <div key={b.branch} className="flex items-center justify-between text-xs" data-testid={`branch-row-${b.branch}`}>
                    <span className="text-emerald-100/90 truncate max-w-[55%]">{b.branch}</span>
                    <span className="font-mono-num font-semibold text-white">{money(b.total, "DOP")}<span className="text-emerald-200/60 ml-1">· {b.count}</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SummaryCard>
        <SummaryCard t={t} label={t("collected_month")} bucket={sum?.month} testId="collections-month-card" onOpen={onOpen} fmtBucket={fmtBucket} countBucket={countBucket}>
          <SummaryGoalBar t={t} goal={goal} progress={progress} />
        </SummaryCard>
      </div>
    </div>
  );
};

export function WhatsAppSettingsPanel() {
  const { t } = useI18n();
  const [f, setF] = useState({ enabled: false, account_sid: "", auth_token: "", from_number: "", admin_whatsapp: "" });
  const [tokenSet, setTokenSet] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [savedEnabled, setSavedEnabled] = useState(false);
  useEffect(() => { api.get("/admin/whatsapp-settings").then((r) => { setF({ enabled: r.data.enabled, account_sid: r.data.account_sid, auth_token: "", from_number: r.data.from_number, admin_whatsapp: r.data.admin_whatsapp }); setTokenSet(r.data.auth_token_set); setSavedEnabled(r.data.enabled); }).catch(() => {}); }, []);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => { setBusy(true); try { await api.put("/admin/whatsapp-settings", f); toast.success(t("content_saved")); setTokenSet(tokenSet || !!f.auth_token); setSavedEnabled(f.enabled); } catch (e) { toast.error("Error"); } finally { setBusy(false); } };
  const sendTest = async () => { setTesting(true); try { await api.post("/admin/whatsapp-test"); toast.success(t("whatsapp_test_ok")); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setTesting(false); } };
  const lbl = "text-emerald-900 font-semibold text-sm";
  return (
    <div className="max-w-xl space-y-5" data-testid="whatsapp-panel">
      <div className="flex items-center gap-2">
        <PaperPlaneTilt size={22} weight="duotone" className="text-emerald-700" />
        <h3 className="font-display font-bold text-emerald-900 text-xl">{t("whatsapp_settings")}</h3>
      </div>
      <p className="text-sm text-slate-500">{t("whatsapp_hint")}</p>
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900" data-testid="whatsapp-sandbox-hint">
        <div className="font-semibold mb-1">{t("whatsapp_sandbox_title")}</div>
        <p>{t("whatsapp_sandbox_hint")}</p>
      </div>
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
        <div className="flex items-center justify-between rounded-lg border border-emerald-100 bg-emerald-50/40 p-3">
          <Label className={lbl}>{t("enable_whatsapp")}</Label>
          <Switch data-testid="whatsapp-enabled" checked={f.enabled} onCheckedChange={(v) => setF((p) => ({ ...p, enabled: v }))} />
        </div>
        <div><Label className={lbl}>{t("twilio_sid")}</Label><Input data-testid="whatsapp-sid" className="mt-1.5 h-11" value={f.account_sid} onChange={set("account_sid")} placeholder="ACxxxxxxxx" /></div>
        <div><Label className={lbl}>{t("twilio_token")}</Label><Input data-testid="whatsapp-token" type="password" className="mt-1.5 h-11" value={f.auth_token} onChange={set("auth_token")} placeholder={tokenSet ? "•••••••• (guardado)" : "Auth Token"} /></div>
        <div><Label className={lbl}>{t("whatsapp_from")}</Label><Input data-testid="whatsapp-from" className="mt-1.5 h-11" value={f.from_number} onChange={set("from_number")} placeholder="whatsapp:+14155238886" /></div>
        <div><Label className={lbl}>{t("admin_whatsapp")}</Label><Input data-testid="whatsapp-admin" className="mt-1.5 h-11" value={f.admin_whatsapp} onChange={set("admin_whatsapp")} placeholder="+1809..." /></div>
        <div className="flex justify-end gap-2">
          <Button data-testid="whatsapp-test" variant="outline" onClick={sendTest} disabled={testing || !savedEnabled} title={!savedEnabled ? t("whatsapp_test_needs_save") : ""} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 disabled:opacity-40">{testing ? t("loading") : t("whatsapp_test")}</Button>
          <Button data-testid="whatsapp-save" onClick={save} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900">{busy ? t("loading") : t("save")}</Button>
        </div>
      </div>
    </div>
  );
}

export function DemographicsPanel({ onFilter }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [top, setTop] = useState([]);
  useEffect(() => {
    api.get("/admin/demographics").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/top-attendees").then((r) => setTop(r.data.top || [])).catch(() => {});
  }, []);
  const Bar = ({ items }) => {
    const max = Math.max(1, ...items.map((x) => x.count));
    return (
      <div className="space-y-2">
        {items.map((x, i) => (
          <button key={i} type="button" onClick={() => x.label !== "—" && onFilter && onFilter(x.label)} disabled={x.label === "—"} className="w-full flex items-center gap-3 text-left disabled:cursor-default group" data-testid={`demo-row-${i}`}>
            <span className="w-32 shrink-0 text-sm text-emerald-900 truncate group-hover:underline">{x.label}</span>
            <div className="flex-1 h-5 rounded-full bg-emerald-50 overflow-hidden">
              <div className="h-full bg-emerald-600 rounded-full group-hover:bg-emerald-700" style={{ width: `${(x.count / max) * 100}%` }} />
            </div>
            <span className="w-10 text-right font-mono-num text-sm text-slate-600">{x.count}</span>
          </button>
        ))}
      </div>
    );
  };
  return (
    <div className="space-y-6" data-testid="demographics-panel">
      <div className="flex items-center gap-2">
        <ChartBar size={22} weight="duotone" className="text-emerald-700" />
        <h3 className="font-display font-bold text-emerald-900 text-xl">{t("demographics_tab")}</h3>
        {data ? <span className="text-sm text-slate-400">· {data.total} {t("members_count")}</span> : null}
      </div>
      {!data ? <p className="text-slate-500">{t("loading")}</p> : (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h4 className="font-display font-bold text-emerald-900 mb-3">{t("by_city")}</h4>
            <Bar items={data.by_city} />
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h4 className="font-display font-bold text-emerald-900 mb-3">{t("by_occupation")}</h4>
            <Bar items={data.by_occupation} />
          </div>
        </div>
      )}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="top-attendees">
        <h4 className="font-display font-bold text-emerald-900 mb-3 flex items-center gap-2"><Trophy size={18} weight="duotone" className="text-amber-500" /> {t("active_members")}</h4>
        {top.length === 0 ? <p className="text-slate-400 text-sm">{t("no_attendees")}</p> : (
          <div className="space-y-2">
            {top.map((m, i) => (
              <div key={m.user_id} className="flex items-center gap-3" data-testid={`top-att-${i}`}>
                <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{i + 1}</span>
                <span className="flex-1 text-sm text-emerald-900 truncate">{m.name}</span>
                <span className="text-sm font-mono-num text-slate-600">{m.count} {t("attendees")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function TeamPanel() {
  const { t } = useI18n();
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const empty = { name: "", role: "", photo_url: "", enabled: true };
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileRefs = useRef({});
  useEffect(() => { api.get("/admin/team").then((r) => setMembers(r.data.members || [])).catch(() => {}); }, []);
  const setM = (i, k, v) => setMembers((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const add = () => setMembers((p) => [...p, { ...empty }]);
  const remove = (i) => { if (!window.confirm(t("confirm_remove_person"))) return; setMembers((p) => p.filter((_, idx) => idx !== i)); };
  const move = (i, dir) => setMembers((p) => {
    const j = i + dir;
    if (j < 0 || j >= p.length) return p;
    const arr = [...p];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr;
  });
  const upload = async (i, file) => {
    if (!file) return;
    setUploading(i);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } }); setM(i, "photo_url", data.url); toast.success("OK"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setUploading(null); }
  };
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/team", { members }); toast.success(t("content_saved")); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="team-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2"><Users size={22} weight="duotone" className="text-emerald-700" /><h3 className="font-display font-bold text-emerald-900 text-xl">{t("manage_team")}</h3></div>
          <p className="text-xs text-slate-400 mt-1">{t("team_hint")}</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="team-add-btn" size="sm" variant="outline" onClick={add} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"><Plus size={16} weight="bold" /> {t("add_member")}</Button>
          <Button data-testid="team-save-btn" size="sm" onClick={save} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900">{busy ? t("loading") : t("save")}</Button>
        </div>
      </div>
      {members.length === 0 ? <p className="text-slate-400 text-sm" data-testid="team-empty">{t("no_team")}</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-list">
          {members.map((m, i) => {
            const initials = (m.name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
            const photo = m.photo_url ? `${BACKEND}${m.photo_url}` : null;
            return (
              <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3" data-testid={`team-member-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="relative">
                    {photo ? <img src={photo} alt="" className="h-16 w-16 rounded-full object-cover border border-emerald-100" /> : <div className="h-16 w-16 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-lg">{initials || "?"}</div>}
                    <button data-testid={`team-upload-${i}`} onClick={() => fileRefs.current[i]?.click()} className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-emerald-700 text-white flex items-center justify-center border-2 border-white hover:bg-emerald-800"><UploadSimple size={12} weight="bold" /></button>
                    <input ref={(el) => (fileRefs.current[i] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => upload(i, e.target.files?.[0])} data-testid={`team-file-${i}`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`team-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} title={t("move_up")} className="p-1 rounded hover:bg-emerald-50 disabled:opacity-30"><CaretUp size={16} className="text-emerald-800" /></button>
                    <button data-testid={`team-down-${i}`} onClick={() => move(i, 1)} disabled={i === members.length - 1} title={t("move_down")} className="p-1 rounded hover:bg-emerald-50 disabled:opacity-30"><CaretDown size={16} className="text-emerald-800" /></button>
                    <Switch data-testid={`team-enabled-${i}`} checked={m.enabled !== false} onCheckedChange={(v) => setM(i, "enabled", v)} />
                    <button data-testid={`team-remove-${i}`} onClick={() => remove(i)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash size={16} weight="bold" /></button>
                  </div>
                </div>
                <div><Label className="text-emerald-900 font-semibold text-xs">{t("full_name")}</Label><Input data-testid={`team-name-${i}`} className="mt-1 h-9" value={m.name} onChange={(e) => setM(i, "name", e.target.value)} /></div>
                <div><Label className="text-emerald-900 font-semibold text-xs">{t("member_role")}</Label><Input data-testid={`team-role-${i}`} className="mt-1 h-9" placeholder="Presidente, Tesorero…" value={m.role} onChange={(e) => setM(i, "role", e.target.value)} /></div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function TestimonialsPanel() {
  const { t } = useI18n();
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const [items, setItems] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const fileRefs = useRef({});
  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const setM = (i, k, v) => setItems((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const add = async () => { try { await api.post("/admin/testimonials", { name: "", role: "", quote: "", status: "approved", enabled: true }); load(); } catch (e) { toast.error("Error"); } };
  const saveOne = async (m) => {
    setSavingId(m.id);
    try { await api.put(`/admin/testimonials/${m.id}`, { name: m.name || "", role: m.role || "", quote: m.quote || "", photo_url: m.photo_url || "", enabled: m.enabled !== false, status: m.status || "approved" }); toast.success(t("content_saved")); load(); }
    catch (e) { toast.error("Error"); } finally { setSavingId(null); }
  };
  const approve = async (id) => { try { await api.post(`/admin/testimonials/${id}/approve`); toast.success(t("status_approved")); load(); } catch (e) { toast.error("Error"); } };
  const remove = async (id) => { if (!window.confirm(t("confirm_remove_person"))) return; try { await api.delete(`/admin/testimonials/${id}`); load(); } catch (e) { toast.error("Error"); } };
  const upload = async (m, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } }); await api.put(`/admin/testimonials/${m.id}`, { name: m.name || "", role: m.role || "", quote: m.quote || "", photo_url: data.url, enabled: m.enabled !== false, status: m.status || "approved" }); toast.success("OK"); load(); }
    catch (e) { toast.error("Error"); }
  };
  const pending = items.filter((x) => x.status === "pending").length;
  return (
    <div className="space-y-6" data-testid="testimonials-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2"><Users size={22} weight="duotone" className="text-emerald-700" /><h3 className="font-display font-bold text-emerald-900 text-xl">{t("manage_testimonials")}</h3>{pending > 0 && <span className="rounded-full bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5" data-testid="pending-count">{pending} {t("status_pending")}</span>}</div>
          <p className="text-xs text-slate-400 mt-1">{t("testimonials_hint")}</p>
        </div>
        <Button data-testid="testimonial-add-btn" size="sm" variant="outline" onClick={add} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"><Plus size={16} weight="bold" /> {t("add_testimonial")}</Button>
      </div>
      {items.length === 0 ? <p className="text-slate-400 text-sm" data-testid="testimonial-empty">{t("no_testimonials")}</p> : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="testimonial-list">
          {items.map((m, i) => {
            const initials = (m.name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
            const photo = m.photo_url ? `${BACKEND}${m.photo_url}` : null;
            const isPending = m.status === "pending";
            return (
              <div key={m.id} className={`bg-white rounded-xl border shadow-sm p-4 space-y-3 ${isPending ? "border-amber-300" : "border-gray-100"}`} data-testid={`testimonial-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="relative">
                    {photo ? <img src={photo} alt="" className="h-14 w-14 rounded-full object-cover border border-emerald-100" /> : <div className="h-14 w-14 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold">{initials || "?"}</div>}
                    <button data-testid={`testimonial-upload-${i}`} onClick={() => fileRefs.current[m.id]?.click()} className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-emerald-700 text-white flex items-center justify-center border-2 border-white hover:bg-emerald-800"><UploadSimple size={12} weight="bold" /></button>
                    <input ref={(el) => (fileRefs.current[m.id] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => upload(m, e.target.files?.[0])} data-testid={`testimonial-file-${i}`} />
                  </div>
                  <div className="flex items-center gap-2">
                    {m.source === "member" && <span className="rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5">{t("member_badge")}</span>}
                    {isPending && <span className="rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5" data-testid={`testimonial-pending-${i}`}>{t("status_pending")}</span>}
                    <button data-testid={`testimonial-remove-${i}`} onClick={() => remove(m.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash size={16} weight="bold" /></button>
                  </div>
                </div>
                {m.rating > 0 && <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={16} weight={n <= m.rating ? "fill" : "regular"} className={n <= m.rating ? "text-amber-400" : "text-slate-300"} />)}</div>}
                <div><Label className="text-emerald-900 font-semibold text-xs">{t("full_name")}</Label><Input data-testid={`testimonial-name-${i}`} className="mt-1 h-9" value={m.name || ""} onChange={(e) => setM(i, "name", e.target.value)} /></div>
                <div><Label className="text-emerald-900 font-semibold text-xs">{t("member_role")}</Label><Input data-testid={`testimonial-role-${i}`} className="mt-1 h-9" value={m.role || ""} onChange={(e) => setM(i, "role", e.target.value)} /></div>
                <div><Label className="text-emerald-900 font-semibold text-xs">{t("testimonial_quote")}</Label><Textarea data-testid={`testimonial-quote-${i}`} className="mt-1" rows={3} value={m.quote || ""} onChange={(e) => setM(i, "quote", e.target.value)} /></div>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <div className="flex items-center gap-2"><Switch data-testid={`testimonial-enabled-${i}`} checked={m.enabled !== false} onCheckedChange={(v) => setM(i, "enabled", v)} /><span className="text-xs text-slate-500">{m.enabled !== false ? t("visible") : t("hidden")}</span></div>
                  <div className="flex gap-2">
                    {isPending && <Button data-testid={`testimonial-approve-${i}`} size="sm" onClick={() => approve(m.id)} className="bg-emerald-600 hover:bg-emerald-700 h-8">{t("approve_testimonial")}</Button>}
                    <Button data-testid={`testimonial-save-${i}`} size="sm" variant="outline" onClick={() => saveOne(m)} disabled={savingId === m.id} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 h-8">{savingId === m.id ? "..." : t("save")}</Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function FaqPanel() {
  const { t } = useI18n();
  const empty = { question: "", answer: "", enabled: true };
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [wa, setWa] = useState("");
  const [waMsg, setWaMsg] = useState("");
  const [cashTag, setCashTag] = useState("");
  const [cashLink, setCashLink] = useState("");
  const [waBusy, setWaBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/faq").then((r) => setItems(r.data.items || [])).catch(() => {});
    api.get("/admin/site-config").then((r) => { setWa(r.data.whatsapp || ""); setWaMsg(r.data.whatsapp_message || ""); setCashTag(r.data.cashapp_tag || ""); setCashLink(r.data.cashapp_link || ""); }).catch(() => {});
  }, []);
  const setM = (i, k, v) => setItems((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const add = () => setItems((p) => [...p, { ...empty }]);
  const remove = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const move = (i, dir) => setItems((p) => { const j = i + dir; if (j < 0 || j >= p.length) return p; const arr = [...p]; [arr[i], arr[j]] = [arr[j], arr[i]]; return arr; });
  const save = async () => { setBusy(true); try { await api.put("/admin/faq", { items }); toast.success(t("content_saved")); } catch (e) { toast.error("Error"); } finally { setBusy(false); } };
  const saveWa = async () => { setWaBusy(true); try { await api.put("/admin/site-config", { whatsapp: wa, whatsapp_message: waMsg, cashapp_tag: cashTag, cashapp_link: cashLink }); toast.success(t("content_saved")); } catch (e) { toast.error("Error"); } finally { setWaBusy(false); } };
  return (
    <div className="space-y-8" data-testid="faq-panel">
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="whatsapp-config">
        <div className="flex items-center gap-2 mb-1"><WhatsappLogo size={20} weight="duotone" className="text-emerald-700" /><h3 className="font-display font-bold text-emerald-900 text-lg">{t("whatsapp_config_title")}</h3></div>
        <p className="text-xs text-slate-400 mb-3">{t("whatsapp_config_hint")}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-emerald-900 font-semibold text-xs">{t("whatsapp_number_label")}</Label><Input data-testid="whatsapp-number-input" className="mt-1 h-10" placeholder="+1 809 000 0000" value={wa} onChange={(e) => setWa(e.target.value)} /></div>
          <div><Label className="text-emerald-900 font-semibold text-xs">{t("whatsapp_message_label")}</Label><Input data-testid="whatsapp-message-input" className="mt-1 h-10" value={waMsg} onChange={(e) => setWaMsg(e.target.value)} /></div>
          <div><Label className="text-emerald-900 font-semibold text-xs">Cash App $cashtag (tienda USA)</Label><Input data-testid="cashapp-tag-input" className="mt-1 h-10" placeholder="$CoopEstrella" value={cashTag} onChange={(e) => setCashTag(e.target.value)} /></div>
          <div><Label className="text-emerald-900 font-semibold text-xs">Cash App enlace (opcional)</Label><Input data-testid="cashapp-link-input" className="mt-1 h-10" placeholder="https://cash.app/$CoopEstrella" value={cashLink} onChange={(e) => setCashLink(e.target.value)} /></div>
        </div>
        <Button data-testid="whatsapp-save-btn" size="sm" onClick={saveWa} disabled={waBusy} className="mt-3 bg-emerald-800 hover:bg-emerald-900">{waBusy ? t("loading") : t("save")}</Button>
      </div>
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2"><Question size={22} weight="duotone" className="text-emerald-700" /><h3 className="font-display font-bold text-emerald-900 text-xl">{t("manage_faq")}</h3></div>
            <p className="text-xs text-slate-400 mt-1">{t("faq_hint")}</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="faq-add-btn" size="sm" variant="outline" onClick={add} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"><Plus size={16} weight="bold" /> {t("add_faq")}</Button>
            <Button data-testid="faq-save-btn" size="sm" onClick={save} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900">{busy ? t("loading") : t("save")}</Button>
          </div>
        </div>
        {items.length === 0 ? <p className="text-slate-400 text-sm" data-testid="faq-empty">{t("no_faq")}</p> : (
          <div className="space-y-3" data-testid="faq-list">
            {items.map((m, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4" data-testid={`faq-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono-num text-slate-400">#{i + 1}</span>
                  <div className="flex items-center gap-2">
                    <button data-testid={`faq-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} className="p-1 rounded hover:bg-emerald-50 disabled:opacity-30"><CaretUp size={16} className="text-emerald-800" /></button>
                    <button data-testid={`faq-down-${i}`} onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 rounded hover:bg-emerald-50 disabled:opacity-30"><CaretDown size={16} className="text-emerald-800" /></button>
                    <Switch data-testid={`faq-enabled-${i}`} checked={m.enabled !== false} onCheckedChange={(v) => setM(i, "enabled", v)} />
                    <button data-testid={`faq-remove-${i}`} onClick={() => remove(i)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash size={16} weight="bold" /></button>
                  </div>
                </div>
                <Input data-testid={`faq-question-${i}`} className="mb-2 h-10 font-semibold" placeholder={t("faq_question")} value={m.question} onChange={(e) => setM(i, "question", e.target.value)} />
                <Textarea data-testid={`faq-answer-${i}`} rows={2} placeholder={t("faq_answer")} value={m.answer} onChange={(e) => setM(i, "answer", e.target.value)} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function NotificationsPanel() {
  const { t } = useI18n();
  const [kinds, setKinds] = useState([]);
  const [flags, setFlags] = useState({});
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const loadLog = () => api.get("/admin/notifications-log?limit=100").then((r) => setLog(r.data.items || [])).catch(() => {});
  useEffect(() => { api.get("/admin/notif-settings").then((r) => { setKinds(r.data.kinds || []); setFlags(r.data.settings || {}); }).catch(() => {}); loadLog(); }, []);
  const save = async () => { setBusy(true); try { await api.put("/admin/notif-settings", flags); toast.success(t("content_saved")); } catch (e) { toast.error("Error"); } finally { setBusy(false); } };
  const labelOf = (k) => (kinds.find((x) => x.key === k) || {}).label || k;
  const allOn = kinds.length > 0 && kinds.every((k) => flags[k.key] !== false);
  const setAll = (v) => setFlags((p) => { const n = { ...p }; kinds.forEach((k) => { n[k.key] = v; }); return n; });
  return (
    <div className="space-y-8" data-testid="notifications-panel">
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2"><EnvelopeSimple size={22} weight="duotone" className="text-emerald-700" /><h3 className="font-display font-bold text-emerald-900 text-xl">Notificaciones</h3></div>
          <div className="flex items-center gap-2">
            <Button data-testid="notif-toggle-all-btn" size="sm" variant="outline" onClick={() => setAll(!allOn)} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">{allOn ? "Desactivar todas" : "Activar todas"}</Button>
            <Button data-testid="notif-save-btn" size="sm" onClick={save} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900">{busy ? t("loading") : t("save")}</Button>
          </div>
        </div>
        <p className="text-sm text-slate-500 mb-4">Activa o desactiva cada aviso del sistema (correo y WhatsApp). Los códigos de seguridad e inicio de sesión siempre se envían.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {kinds.map((k) => (
            <div key={k.key} className="flex items-center justify-between bg-white rounded-xl border border-gray-100 shadow-sm p-4" data-testid={`notif-toggle-${k.key}`}>
              <span className="text-sm font-semibold text-emerald-900 pr-3">{k.label}</span>
              <Switch data-testid={`notif-switch-${k.key}`} checked={flags[k.key] !== false} onCheckedChange={(v) => setFlags((p) => ({ ...p, [k.key]: v }))} />
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-emerald-900 text-lg">Historial de envíos</h3>
          <button data-testid="notif-refresh" onClick={loadLog} className="text-xs font-semibold text-emerald-700 hover:underline inline-flex items-center gap-1"><ArrowsClockwise size={14} weight="bold" /> Actualizar</button>
        </div>
        {log.length === 0 ? <p className="text-slate-400 text-sm" data-testid="notif-log-empty">Aún no hay notificaciones enviadas.</p> : (
          <div className="overflow-x-auto border border-gray-100 rounded-xl">
            <table className="w-full text-sm" data-testid="notif-log">
              <thead><tr className="text-left text-slate-400 border-b border-gray-100"><th className="px-3 py-2 font-medium">Fecha</th><th className="px-3 py-2 font-medium">Aviso</th><th className="px-3 py-2 font-medium">Canal</th><th className="px-3 py-2 font-medium">Para</th><th className="px-3 py-2 font-medium">Estado</th></tr></thead>
              <tbody>
                {log.map((n) => (
                  <tr key={n.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-3 py-2 text-slate-600">{(n.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2 text-emerald-900 font-semibold">{labelOf(n.kind)}</td>
                    <td className="px-3 py-2">{n.channel === "email" ? <span className="inline-flex items-center gap-1 text-slate-600"><EnvelopeSimple size={14} /> Correo</span> : n.channel === "whatsapp" ? <span className="inline-flex items-center gap-1 text-emerald-700"><WhatsappLogo size={14} weight="fill" /> WhatsApp</span> : <span className="text-slate-500 text-xs">Varios</span>}</td>
                    <td className="px-3 py-2 text-slate-500 font-mono-num text-xs">{n.to}</td>
                    <td className="px-3 py-2">{n.ok ? <CheckCircle size={16} weight="fill" className="text-emerald-600" /> : <Prohibit size={16} weight="fill" className="text-red-500" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export function CarouselPanel() {
  const { t } = useI18n();
  const emptySlide = { title: "", text: "", image_url: "", button_text: "", button_link: "", button_target: "url", person_id: "", person_name: "", enabled: true };
  const [slides, setSlides] = useState([]);
  const [anns, setAnns] = useState([]);
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileRefs = useRef({});
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get("/admin/carousel").then((r) => { setSlides(r.data.slides || []); setAnns(r.data.announcements || []); }).catch(() => {});
    api.get("/admin/clients").then((r) => setClients(r.data.map((c) => c.user))).catch(() => {});
  }, []);

  const setS = (i, k, v) => setSlides((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addSlide = () => { if (slides.length >= 3) { toast.error(t("max_slides")); return; } setSlides((p) => [...p, { ...emptySlide }]); };
  const removeSlide = (i) => { if (!window.confirm(t("confirm_remove"))) return; setSlides((p) => p.filter((_, idx) => idx !== i)); };
  const setA = (i, k, v) => setAnns((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addAnn = () => setAnns((p) => [...p, { title: "", text: "", enabled: true }]);
  const removeAnn = (i) => { if (!window.confirm(t("confirm_remove"))) return; setAnns((p) => p.filter((_, idx) => idx !== i)); };

  const upload = async (i, file) => {
    if (!file) return;
    setUploading(i);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } }); setS(i, "image_url", data.url); toast.success("OK"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setUploading(null); }
  };

  const assignPerson = (i, id) => {
    const c = clients.find((x) => x.id === id);
    setSlides((p) => p.map((x, idx) => (idx === i ? { ...x, person_id: id || "", person_name: c ? c.name : "" } : x)));
  };

  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/carousel", { slides, announcements: anns }); toast.success(t("content_saved")); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const lbl = "text-emerald-900 font-semibold text-xs";
  return (
    <div className="space-y-6" data-testid="carousel-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2"><Image size={22} weight="duotone" className="text-emerald-700" /><h3 className="font-display font-bold text-emerald-900 text-xl">{t("manage_carousel")}</h3></div>
          <p className="text-xs text-slate-400 mt-1">{t("carousel_hint")}</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="carousel-add-slide" size="sm" variant="outline" onClick={addSlide} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"><Plus size={16} weight="bold" /> {t("add_slide")}</Button>
          <Button data-testid="carousel-save-btn" size="sm" onClick={save} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900">{busy ? t("loading") : t("save")}</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {slides.map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3" data-testid={`carousel-slide-${i}`}>
            <div className="flex items-center justify-between">
              <h4 className="font-display font-bold text-emerald-900">{t("slide")} {i + 1}</h4>
              <div className="flex items-center gap-2">
                <Switch data-testid={`carousel-enabled-${i}`} checked={s.enabled !== false} onCheckedChange={(v) => setS(i, "enabled", v)} />
                <button data-testid={`carousel-remove-${i}`} onClick={() => removeSlide(i)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title={t("remove_slide")}><Trash size={16} weight="bold" /></button>
              </div>
            </div>
            <div className="relative aspect-video rounded-lg overflow-hidden bg-emerald-50 border border-emerald-100 flex items-center justify-center">
              {s.image_url ? <img src={`${BACKEND}${s.image_url}`} alt="" className="w-full h-full object-cover" /> : <Image size={30} className="text-emerald-300" />}
              <button data-testid={`carousel-upload-${i}`} onClick={() => fileRefs.current[i]?.click()} className="absolute bottom-2 right-2 h-8 px-2.5 rounded-lg bg-emerald-800 text-white text-xs flex items-center gap-1 hover:bg-emerald-900">
                <UploadSimple size={14} weight="bold" /> {uploading === i ? "…" : t("slide_image")}
              </button>
              <input ref={(el) => (fileRefs.current[i] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => upload(i, e.target.files?.[0])} data-testid={`carousel-file-${i}`} />
            </div>
            <div><Label className={lbl}>{t("slide_title")}</Label><Input data-testid={`carousel-title-${i}`} className="mt-1 h-9" value={s.title} onChange={(e) => setS(i, "title", e.target.value)} /></div>
            <div><Label className={lbl}>{t("slide_text")}</Label><Textarea data-testid={`carousel-text-${i}`} className="mt-1" rows={2} value={s.text} onChange={(e) => setS(i, "text", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className={lbl}>{t("button_text")}</Label><Input data-testid={`carousel-btntext-${i}`} className="mt-1 h-9" value={s.button_text} onChange={(e) => setS(i, "button_text", e.target.value)} /></div>
              <div>
                <Label className={lbl}>{t("btn_target")}</Label>
                <select data-testid={`carousel-btntarget-${i}`} value={s.button_target || "url"} onChange={(e) => setS(i, "button_target", e.target.value)} className="mt-1 w-full h-9 rounded-md border border-gray-200 px-2 text-sm text-emerald-900 bg-white">
                  <option value="url">{t("btn_target_url")}</option>
                  <option value="eventos">{t("btn_target_events")}</option>
                </select>
              </div>
            </div>
            {(s.button_target || "url") === "url" && (
              <div><Label className={lbl}>{t("button_link")}</Label><Input data-testid={`carousel-btnlink-${i}`} className="mt-1 h-9" placeholder="https://" value={s.button_link} onChange={(e) => setS(i, "button_link", e.target.value)} /></div>
            )}
            <div>
              <Label className={lbl}>{t("assign_person")}</Label>
              <select data-testid={`carousel-person-${i}`} value={s.person_id || ""} onChange={(e) => assignPerson(i, e.target.value)} className="mt-1 w-full h-9 rounded-md border border-gray-200 px-2 text-sm text-emerald-900 bg-white">
                <option value="">{t("none_option")}</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {s.person_id ? <button data-testid={`carousel-person-clear-${i}`} onClick={() => assignPerson(i, "")} className="mt-1 text-xs text-red-600 hover:underline">{t("remove_person")}</button> : null}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display font-bold text-emerald-900 flex items-center gap-2"><EnvelopeSimple size={18} weight="duotone" /> {t("announcements")}</h4>
          <Button data-testid="ann-add-btn" size="sm" variant="outline" onClick={addAnn} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"><Plus size={15} weight="bold" /> {t("add_announcement")}</Button>
        </div>
        {anns.length === 0 ? <p className="text-slate-400 text-sm" data-testid="ann-empty">{t("no_announcements")}</p> : (
          <div className="space-y-3" data-testid="ann-list">
            {anns.map((a, i) => (
              <div key={i} className="border border-gray-100 rounded-lg p-3 space-y-2" data-testid={`ann-${i}`}>
                <div className="flex items-center gap-2">
                  <Input data-testid={`ann-title-${i}`} placeholder={t("slide_title")} className="h-9 flex-1" value={a.title} onChange={(e) => setA(i, "title", e.target.value)} />
                  <Switch data-testid={`ann-enabled-${i}`} checked={a.enabled !== false} onCheckedChange={(v) => setA(i, "enabled", v)} />
                  <button data-testid={`ann-remove-${i}`} onClick={() => removeAnn(i)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash size={16} weight="bold" /></button>
                </div>
                <Textarea data-testid={`ann-text-${i}`} placeholder={t("slide_text")} rows={2} value={a.text} onChange={(e) => setA(i, "text", e.target.value)} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function ContentPanel() {
  const { t } = useI18n();
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const emptyBlock = { title: "", text: "", image_url: "", enabled: true, button_text: "", button_link: "" };
  const [blocks, setBlocks] = useState([{ ...emptyBlock }, { ...emptyBlock }]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);

  useEffect(() => {
    api.get("/landing").then((r) => {
      if (Array.isArray(r.data) && r.data.length) {
        setBlocks(r.data.map((b) => ({ ...emptyBlock, ...b })));
      }
    }).catch(() => {});
  }, []); // eslint-disable-line

  const setB = (i, k, v) => setBlocks((prev) => prev.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addBlock = () => setBlocks((prev) => [...prev, { ...emptyBlock }]);
  const removeBlock = (i) => setBlocks((prev) => prev.filter((_, idx) => idx !== i));
  const move = (i, dir) => setBlocks((prev) => {
    const j = i + dir;
    if (j < 0 || j >= prev.length) return prev;
    const arr = [...prev];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr;
  });

  const upload = async (i, file) => {
    if (!file) return;
    setUploading(i);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setB(i, "image_url", data.url);
      toast.success("OK");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setUploading(null); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/landing", { blocks });
      toast.success(t("content_saved"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="content-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Image size={22} weight="duotone" className="text-emerald-700" />
          <h3 className="font-display font-bold text-emerald-900 text-xl">{t("landing_content")}</h3>
        </div>
        <div className="flex gap-2">
          <Button data-testid="content-add-btn" size="sm" variant="outline" onClick={addBlock} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"><Plus size={16} weight="bold" /> {t("add_window")}</Button>
          <Button data-testid="content-save-btn" size="sm" onClick={save} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900">{busy ? t("loading") : t("save_content")}</Button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {blocks.map((b, i) => (
          <div key={i} className={`bg-white rounded-xl border shadow-sm p-5 space-y-4 ${b.enabled === false ? "border-gray-200 opacity-70" : "border-gray-100"}`} data-testid={`content-block-${i}`}>
            <div className="flex items-center justify-between">
              <h4 className="font-display font-bold text-emerald-900">{t("block_win")} {i + 1}</h4>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 mr-1">
                  <Switch data-testid={`content-enabled-${i}`} checked={b.enabled !== false} onCheckedChange={(v) => setB(i, "enabled", v)} />
                  <span className="text-xs text-slate-500">{t("visible")}</span>
                </div>
                <button data-testid={`content-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} title={t("move_up")} className="p-1.5 rounded hover:bg-emerald-50 disabled:opacity-30"><CaretUp size={16} className="text-emerald-800" /></button>
                <button data-testid={`content-down-${i}`} onClick={() => move(i, 1)} disabled={i === blocks.length - 1} title={t("move_down")} className="p-1.5 rounded hover:bg-emerald-50 disabled:opacity-30"><CaretDown size={16} className="text-emerald-800" /></button>
                <button data-testid={`content-remove-${i}`} onClick={() => removeBlock(i)} title={t("remove_window")} className="p-1.5 rounded hover:bg-red-50"><Trash size={16} className="text-red-500" /></button>
              </div>
            </div>
            <div>
              <Label className="text-emerald-900 font-semibold">{t("block_title")}</Label>
              <Input data-testid={`content-title-${i}`} className="mt-1.5 h-11" value={b.title} onChange={(e) => setB(i, "title", e.target.value)} />
            </div>
            <div>
              <Label className="text-emerald-900 font-semibold">{t("block_text")}</Label>
              <Textarea data-testid={`content-text-${i}`} className="mt-1.5 min-h-28" value={b.text} onChange={(e) => setB(i, "text", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-emerald-900 font-semibold text-sm">{t("button_text")}</Label>
                <Input data-testid={`content-btntext-${i}`} className="mt-1.5 h-10" value={b.button_text} onChange={(e) => setB(i, "button_text", e.target.value)} />
              </div>
              <div>
                <Label className="text-emerald-900 font-semibold text-sm">{t("button_link")}</Label>
                <Input data-testid={`content-btnlink-${i}`} className="mt-1.5 h-10" placeholder="https://" value={b.button_link} onChange={(e) => setB(i, "button_link", e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-emerald-900 font-semibold">{t("block_image")}</Label>
              {b.image_url && (
                <div className="mt-2 aspect-[16/9] w-full overflow-hidden rounded-lg border border-gray-100 bg-emerald-50">
                  <img src={`${BACKEND}${b.image_url}`} alt="preview" className="h-full w-full object-cover" data-testid={`content-img-${i}`} />
                </div>
              )}
              <label className="mt-2 inline-flex items-center gap-2 cursor-pointer text-sm font-semibold text-emerald-800 border border-emerald-300 rounded-md px-3 py-2 hover:bg-emerald-50 transition-colors">
                <UploadSimple size={16} weight="bold" />
                {uploading === i ? t("uploading") : t("upload_image")}
                <input data-testid={`content-file-${i}`} type="file" accept="image/*" className="hidden" onChange={(e) => upload(i, e.target.files?.[0])} disabled={uploading !== null} />
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RatesPanel({ rates, onSaved }) {
  const { t, lang } = useI18n();
  const [s, setS] = useState(rates.savings_rate);
  const [sh, setSh] = useState(rates.shares_rate);
  const [ex, setEx] = useState(rates.exchange_rate ?? 60);
  const [nd, setNd] = useState(rates.maturity_notice_days ?? 5);
  const [sld, setSld] = useState(rates.savings_lock_notice_days ?? 5);
  const [pen, setPen] = useState(rates.td_penalty_pct ?? 5);
  const [cofn, setCofn] = useState(rates.cert_officer_name ?? "");
  const [coft, setCoft] = useState(rates.cert_officer_title ?? "");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/admin/rates/history").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { setS(rates.savings_rate); setSh(rates.shares_rate); setEx(rates.exchange_rate ?? 60); setNd(rates.maturity_notice_days ?? 5); setSld(rates.savings_lock_notice_days ?? 5); setPen(rates.td_penalty_pct ?? 5); setCofn(rates.cert_officer_name ?? ""); setCoft(rates.cert_officer_title ?? ""); }, [rates]);
  useEffect(() => { loadHistory(); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/rates", { savings_rate: Number(s), shares_rate: Number(sh), exchange_rate: Number(ex), maturity_notice_days: Number(nd), savings_lock_notice_days: Number(sld), td_penalty_pct: Number(pen), cert_officer_name: cofn, cert_officer_title: coft });
      toast.success(t("rates_updated"));
      onSaved(data);
      loadHistory();
    } catch (e) {
      toast.error("Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-xl">
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="rates-panel">
      <div className="flex items-center gap-2 mb-6">
        <Percent size={22} weight="duotone" className="text-emerald-700" />
        <h3 className="font-display font-bold text-emerald-900 text-xl">{t("manage_rates")}</h3>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="text-emerald-900 font-semibold">{t("savings_rate")} (%)</Label>
          <Input data-testid="rate-savings-input" type="number" step="0.1" className="mt-1.5 h-11 font-mono-num" value={s} onChange={(e) => setS(e.target.value)} />
        </div>
        <div>
          <Label className="text-emerald-900 font-semibold">{t("shares_rate")} (%)</Label>
          <Input data-testid="rate-shares-input" type="number" step="0.1" className="mt-1.5 h-11 font-mono-num" value={sh} onChange={(e) => setSh(e.target.value)} />
        </div>
      </div>
      <div className="mt-5">
        <Label className="text-emerald-900 font-semibold">{t("exchange_rate")}</Label>
        <Input data-testid="rate-exchange-input" type="number" step="0.01" className="mt-1.5 h-11 font-mono-num max-w-xs" value={ex} onChange={(e) => setEx(e.target.value)} />
        <p className="text-xs text-slate-400 mt-1.5">{t("exchange_hint")}</p>
      </div>
      <div className="mt-5">
        <Label className="text-emerald-900 font-semibold">{t("maturity_notice_days")}</Label>
        <Input data-testid="rate-notice-days-input" type="number" min="0" max="90" className="mt-1.5 h-11 font-mono-num max-w-xs" value={nd} onChange={(e) => setNd(e.target.value)} />
      </div>
      <div className="mt-5">
        <Label className="text-emerald-900 font-semibold">{t("savings_lock_notice_days")}</Label>
        <Input data-testid="rate-savings-lock-days-input" type="number" min="0" max="90" className="mt-1.5 h-11 font-mono-num max-w-xs" value={sld} onChange={(e) => setSld(e.target.value)} />
      </div>
      <div className="grid sm:grid-cols-2 gap-5 mt-5">
        <div>
          <Label className="text-emerald-900 font-semibold">{t("cert_officer_name")}</Label>
          <Input data-testid="rate-officer-name-input" className="mt-1.5 h-11" value={cofn} onChange={(e) => setCofn(e.target.value)} placeholder="Ej: Juan Pérez" />
        </div>
        <div>
          <Label className="text-emerald-900 font-semibold">{t("cert_officer_title")}</Label>
          <Input data-testid="rate-officer-title-input" className="mt-1.5 h-11" value={coft} onChange={(e) => setCoft(e.target.value)} placeholder="Ej: Gerente General" />
        </div>
      </div>
      <div className="mt-5">
        <Label className="text-emerald-900 font-semibold">{t("td_penalty_default")}</Label>
        <Input data-testid="rate-td-penalty-input" type="number" min="0" max="100" step="0.5" className="mt-1.5 h-11 font-mono-num max-w-xs" value={pen} onChange={(e) => setPen(e.target.value)} />
        <p className="text-xs text-slate-400 mt-1.5">{t("td_penalty_default_hint")}</p>
      </div>
      <Button data-testid="rates-save-btn" onClick={save} disabled={saving} className="mt-6 bg-emerald-800 hover:bg-emerald-900">
        {saving ? t("loading") : t("update_rates")}
      </Button>
    </div>

    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="rate-history-panel">
      <div className="flex items-center gap-2 mb-4">
        <ClockCounterClockwise size={20} weight="duotone" className="text-emerald-700" />
        <h4 className="font-display font-bold text-emerald-900">{t("rate_history")}</h4>
      </div>
      {history.length === 0 ? (
        <p className="text-slate-400 text-sm">{t("no_history")}</p>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="rate-history-table">
            <TableHeader><TableRow>
              <TableHead>{t("audit_date")}</TableHead>
              <TableHead>{t("savings_rate")}</TableHead>
              <TableHead>{t("shares_rate")}</TableHead>
              <TableHead>{t("exchange_rate")}</TableHead>
              <TableHead>{t("changed_by")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {history.map((h, i) => (
                <TableRow key={i} data-testid="rate-history-row">
                  <TableCell className="text-xs text-slate-400 whitespace-nowrap">{dateFmt(h.created_at, lang)}</TableCell>
                  <TableCell className="font-mono-num">{h.savings_rate}%</TableCell>
                  <TableCell className="font-mono-num">{h.shares_rate}%</TableCell>
                  <TableCell className="font-mono-num">{h.exchange_rate != null ? `RD$${h.exchange_rate}` : "—"}</TableCell>
                  <TableCell className="text-sm text-emerald-900">{h.changed_by || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
    </div>
  );
}

export async function downloadBlob(url, filename) {
  try { const r = await api.get(url, { responseType: "blob" }); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement("a"); a.href = u; a.download = filename; a.click(); window.URL.revokeObjectURL(u); }
  catch { toast.error("Error al descargar"); }
}

export function EmployeeDialog({ editing, onDone, trigger }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const blank = { name: "", address: "", phone: "", email: "", national_id: "", salary: "", currency: "DOP", active: true };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(editing ? { ...editing, salary: String(editing.salary ?? "") } : blank); }, [open, editing]); // eslint-disable-line
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.name) { toast.error(t("employee_name")); return; }
    setBusy(true);
    const body = { name: f.name, address: f.address, phone: f.phone, email: f.email, national_id: f.national_id, salary: Number(f.salary || 0), currency: f.currency, active: f.active };
    try { if (editing) await api.put(`/admin/employees/${editing.id}`, body); else await api.post("/admin/employees", body); toast.success(t("employee_saved")); setOpen(false); await onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="employee-dialog">
        <DialogHeader><DialogTitle className="font-display text-emerald-900">{editing ? t("edit_employee") : t("new_employee")}</DialogTitle><DialogDescription className="sr-only">{t("new_employee")}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-emerald-900 font-semibold">{t("employee_name")}</Label><Input data-testid="employee-name" className="mt-1 h-11" value={f.name} onChange={set("name")} /></div>
          <div><Label className="text-emerald-900 font-semibold">{t("national_id")}</Label><Input data-testid="employee-cedula" className="mt-1 h-11" value={f.national_id} onChange={set("national_id")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-emerald-900 font-semibold">{t("phone")}</Label><Input data-testid="employee-phone" className="mt-1 h-11" value={f.phone} onChange={set("phone")} /></div>
            <div><Label className="text-emerald-900 font-semibold">{t("email")}</Label><Input data-testid="employee-email" className="mt-1 h-11" value={f.email} onChange={set("email")} /></div>
          </div>
          <div><Label className="text-emerald-900 font-semibold">{t("address")}</Label><Input data-testid="employee-address" className="mt-1 h-11" value={f.address} onChange={set("address")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-emerald-900 font-semibold">{t("salary")}</Label><Input data-testid="employee-salary" type="number" className="mt-1 h-11 font-mono-num" value={f.salary} onChange={set("salary")} /></div>
            <div><Label className="text-emerald-900 font-semibold">{t("currency")}</Label>
              <Select value={f.currency} onValueChange={(v) => setF({ ...f, currency: v })}><SelectTrigger data-testid="employee-currency" className="mt-1 h-11"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="DOP">{t("dop")}</SelectItem><SelectItem value="USD">{t("usd")}</SelectItem></SelectContent></Select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="employee-active" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="h-4 w-4 accent-emerald-700" /> {t("active")}</label>
        </div>
        <DialogFooter><Button data-testid="employee-save" onClick={submit} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900">{t("confirm")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PayrollDialog({ employee, trigger }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [amount, setAmount] = useState(String(employee.salary ?? ""));
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/employees/${employee.id}/payments`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { if (open) { load(); setPeriod(new Date().toISOString().slice(0, 7)); setAmount(String(employee.salary ?? "")); } }, [open]); // eslint-disable-line
  const pay = async () => { setBusy(true); try { await api.post(`/admin/employees/${employee.id}/pay`, { period, amount: Number(amount || 0), note: "" }); toast.success(t("payment_registered")); await load(); } catch { toast.error("Error"); } finally { setBusy(false); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="payroll-dialog">
        <DialogHeader><DialogTitle className="font-display text-emerald-900">{t("payroll_history")} — {employee.name}</DialogTitle><DialogDescription className="sr-only">{t("payroll_history")}</DialogDescription></DialogHeader>
        <div className="flex items-end gap-2">
          <div className="flex-1"><Label className="text-emerald-900 font-semibold text-xs">{t("period")}</Label><Input data-testid="payroll-period" type="month" className="mt-1 h-10" value={period} onChange={(e) => setPeriod(e.target.value)} /></div>
          <div className="flex-1"><Label className="text-emerald-900 font-semibold text-xs">{t("amount")}</Label><Input data-testid="payroll-amount" type="number" className="mt-1 h-10 font-mono-num" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
          <Button data-testid="payroll-pay-btn" onClick={pay} disabled={busy || !period} className="h-10 bg-emerald-800 hover:bg-emerald-900">{t("register_payment")}</Button>
        </div>
        <div className="mt-4 max-h-64 overflow-y-auto">
          {items.length === 0 ? <p className="text-slate-400 text-sm text-center py-4" data-testid="payroll-empty">{t("no_payments")}</p> : (
            <div className="space-y-1.5" data-testid="payroll-list">{items.map((p) => (
              <div key={p.id} className="flex justify-between items-center text-sm border border-gray-100 rounded-lg px-3 py-2">
                <span className="font-semibold text-emerald-900">{p.period}</span>
                <span className="font-mono-num">{money(p.amount, p.currency)}</span>
                <span className="text-xs text-slate-400">{(p.created_at || "").slice(0, 10)}</span>
              </div>
            ))}</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EmployeesPanel() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/admin/employees").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const active = items.filter((e) => e.active);
  const total = active.reduce((a, e) => a + (Number(e.salary) || 0), 0);
  const remove = async (id) => { if (!window.confirm(t("confirm_delete"))) return; try { await api.delete(`/admin/employees/${id}`); toast.success(t("employee_deleted")); load(); } catch { toast.error("Error"); } };
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="employees-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2"><Users size={22} weight="duotone" className="text-emerald-700" /><h3 className="font-display font-bold text-emerald-900 text-xl">{t("employees_tab")}</h3></div>
        <div className="flex gap-1.5 flex-wrap">
          <Button data-testid="export-employees-xlsx-btn" size="sm" variant="outline" onClick={() => downloadBlob("/admin/employees/export.xlsx", "nomina.xlsx")} className="gap-1 border-emerald-300 text-emerald-800 hover:bg-emerald-50"><FileXls size={15} weight="bold" /> Excel</Button>
          <Button data-testid="export-employees-pdf-btn" size="sm" variant="outline" onClick={() => downloadBlob("/admin/employees/export.pdf", "nomina.pdf")} className="gap-1 border-red-200 text-red-700 hover:bg-red-50"><FilePdf size={15} weight="bold" /> PDF</Button>
          <EmployeeDialog onDone={load} trigger={<Button data-testid="new-employee-btn" size="sm" className="gap-1 bg-emerald-800 hover:bg-emerald-900"><Plus size={15} weight="bold" /> {t("new_employee")}</Button>} />
        </div>
      </div>
      <div className="mb-4 rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-sm text-emerald-900">{t("total_payroll")}: <b className="font-mono-num" data-testid="total-payroll">{money(total, "DOP")}</b> <span className="text-slate-400">· {active.length}</span></div>
      {loading ? <p className="text-slate-500 py-4">{t("loading")}</p> : items.length === 0 ? <p className="text-slate-400 py-6 text-center" data-testid="employees-empty">{t("no_employees")}</p> : (
        <div className="overflow-x-auto"><Table data-testid="employees-table">
          <TableHeader><TableRow><TableHead>{t("employee_name")}</TableHead><TableHead>{t("national_id")}</TableHead><TableHead>{t("phone")}</TableHead><TableHead>{t("email")}</TableHead><TableHead>{t("salary")}</TableHead><TableHead className="text-right">{t("actions")}</TableHead></TableRow></TableHeader>
          <TableBody>{items.map((e) => (
            <TableRow key={e.id} data-testid={`employee-row-${e.id}`}>
              <TableCell><div className="font-semibold text-emerald-900">{e.name}</div><div className="text-xs text-slate-400">{e.address}</div>{!e.active && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{t("inactive")}</span>}</TableCell>
              <TableCell className="font-mono-num">{e.national_id || "—"}</TableCell>
              <TableCell>{e.phone || "—"}</TableCell>
              <TableCell className="text-xs">{e.email || "—"}</TableCell>
              <TableCell className="font-mono-num font-semibold">{money(e.salary, e.currency)}</TableCell>
              <TableCell className="text-right"><div className="flex gap-1.5 justify-end">
                <PayrollDialog employee={e} trigger={<Button data-testid={`pay-employee-${e.id}`} size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50" title={t("pay_salary")}><PaperPlaneTilt size={15} weight="bold" /></Button>} />
                <EmployeeDialog editing={e} onDone={load} trigger={<Button data-testid={`edit-employee-${e.id}`} size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"><PencilSimple size={15} weight="bold" /></Button>} />
                <Button data-testid={`delete-employee-${e.id}`} size="sm" variant="outline" onClick={() => remove(e.id)} className="border-red-200 text-red-700 hover:bg-red-50"><Trash size={15} weight="bold" /></Button>
              </div></TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></div>
      )}
    </div>
  );
}

export function LiquidationsPanel() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/liquidations").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="liquidations-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2"><Coins size={22} weight="duotone" className="text-orange-600" /><h3 className="font-display font-bold text-emerald-900 text-xl">{t("liquidations_tab")}</h3></div>
        <Button data-testid="export-liquidations-btn" size="sm" variant="outline" onClick={() => downloadBlob("/admin/liquidations/export.xlsx", "liquidaciones.xlsx")} className="gap-1 border-emerald-300 text-emerald-800 hover:bg-emerald-50"><FileXls size={15} weight="bold" /> Excel</Button>
      </div>
      {loading ? <p className="text-slate-500 py-4">{t("loading")}</p> : items.length === 0 ? <p className="text-slate-400 py-6 text-center" data-testid="liquidations-empty">{t("no_liquidations")}</p> : (
        <div className="overflow-x-auto"><Table data-testid="liquidations-table">
          <TableHeader><TableRow><TableHead>{t("date")}</TableHead><TableHead>{t("account_number")}</TableHead><TableHead>{t("holder")}</TableHead><TableHead>{t("principal")}</TableHead><TableHead>{t("td_penalty_amount")}</TableHead><TableHead>{t("td_net_payout")}</TableHead><TableHead>{t("authorized_by")}</TableHead></TableRow></TableHeader>
          <TableBody>{items.map((x) => (
            <TableRow key={x.id} data-testid={`liquidation-row-${x.id}`}>
              <TableCell className="text-xs">{(x.created_at || "").slice(0, 10)}</TableCell>
              <TableCell className="font-mono-num text-emerald-800">{x.account_number}</TableCell>
              <TableCell>{x.full_name || "—"}</TableCell>
              <TableCell className="font-mono-num">{money(x.principal, x.currency)}</TableCell>
              <TableCell className="font-mono-num text-red-700">− {money(x.penalty_amount, x.currency)} <span className="text-xs text-slate-400">({x.penalty_pct}%)</span></TableCell>
              <TableCell className="font-mono-num font-bold">{money(x.net_payout, x.currency)}{x.deposited_to_savings && <span className="ml-1 text-[10px] text-emerald-600">✓</span>}</TableCell>
              <TableCell className="text-xs">{x.liquidated_by || "—"}</TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></div>
      )}
    </div>
  );
}

export function AccBadge({ on, icon: Icon, label, value }) {
  return (
    <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold border ${on ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-400"}`}>
      <Icon size={13} weight="duotone" />
      <span>{label}</span>
      {on && value ? <span className="font-mono-num">· {value}</span> : null}
    </div>
  );
}

export const PROFILE_FIELD_LABELS = {
  name: "full_name", email: "email", phone: "phone", national_id: "national_id",
  address: "address", city: "city", date_of_birth: "date_of_birth",
  occupation: "occupation", notes: "internal_notes", branch: "branch",
};

export function EditClientProfileDialog({ client, onDone, trigger }) {
  const { t } = useI18n();
  const u = client.user;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [branches, setBranches] = useState([]);
  const [photoUrl, setPhotoUrl] = useState(u.photo_url || null);
  const fileRef = useRef(null);
  const blank = () => ({
    name: u.name || "", email: u.email || "", phone: u.phone || "", national_id: u.national_id || "",
    address: u.address || "", city: u.city || "", date_of_birth: u.date_of_birth || "",
    occupation: u.occupation || "", notes: u.notes || "", branch: u.branch || "",
  });
  const [f, setF] = useState(blank());
  const loadHistory = () => api.get(`/admin/clients/${u.id}/profile-history`).then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { if (open) { setF(blank()); setPhotoUrl(u.photo_url || null); loadHistory(); api.get("/admin/branches").then((r) => setBranches(r.data.branches || [])).catch(() => {}); } }, [open]); // eslint-disable-line
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/admin/clients/${u.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setPhotoUrl(data.photo_url); toast.success(t("photo_updated")); await onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const removePhoto = async () => {
    try { await api.delete(`/admin/clients/${u.id}/photo`); setPhotoUrl(null); toast.success(t("photo_updated")); await onDone(); }
    catch (err) { toast.error("Error"); }
  };
  const save = async () => {
    if (!f.name.trim()) { toast.error(t("name_required")); return; }
    setBusy(true);
    try {
      await api.put(`/admin/clients/${u.id}/profile`, {
        name: f.name, email: f.email, phone: f.phone, national_id: f.national_id,
        address: f.address, city: f.city, date_of_birth: f.date_of_birth,
        occupation: f.occupation, notes: f.notes, branch: f.branch,
      });
      toast.success(t("profile_updated"));
      await loadHistory();
      await onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const fld = "mt-1.5 h-11";
  const lbl = "text-emerald-900 font-semibold text-sm";
  const initials = (u.name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  const photoSrc = photoUrl ? `${process.env.REACT_APP_BACKEND_URL}${photoUrl}` : null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="edit-profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900 flex items-center gap-2"><PencilSimple size={20} weight="duotone" /> {t("edit_profile")}</DialogTitle>
          <DialogDescription className="sr-only">{t("personal_info")}</DialogDescription>
        </DialogHeader>

        {/* Photo */}
        <div className="flex items-center gap-4 pb-3 border-b border-gray-100">
          {photoSrc ? (
            <img src={photoSrc} alt={u.name} className="h-16 w-16 rounded-full object-cover border border-emerald-100" data-testid="profile-photo-preview" />
          ) : (
            <div className="h-16 w-16 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-xl">{initials || "?"}</div>
          )}
          <div>
            <p className="text-sm font-semibold text-emerald-900 mb-1.5">{t("profile_photo")}</p>
            <div className="flex gap-2">
              <Button type="button" data-testid="profile-photo-upload" size="sm" variant="outline" className="gap-1 border-emerald-300 text-emerald-800 hover:bg-emerald-50" onClick={() => fileRef.current?.click()}><UploadSimple size={14} weight="bold" /> {t("upload_photo")}</Button>
              {photoSrc && <Button type="button" data-testid="profile-photo-remove" size="sm" variant="outline" className="gap-1 border-red-200 text-red-700 hover:bg-red-50" onClick={removePhoto}><Trash size={14} weight="bold" /> {t("remove_photo")}</Button>}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="profile-photo-file" />
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label className={lbl}>{t("full_name")}</Label><Input data-testid="profile-name" className={fld} value={f.name} onChange={set("name")} /></div>
          <div><Label className={lbl}>{t("national_id")}</Label><Input data-testid="profile-national-id" className={fld} placeholder={t("national_id_ph")} value={f.national_id} onChange={set("national_id")} /></div>
          <div><Label className={lbl}>{t("date_of_birth")}</Label><Input data-testid="profile-dob" type="date" className={fld} value={f.date_of_birth} onChange={set("date_of_birth")} /></div>
          <div><Label className={lbl}>{t("email")}</Label><Input data-testid="profile-email" type="email" className={fld} value={f.email} onChange={set("email")} /></div>
          <div><Label className={lbl}>{t("phone")}</Label><Input data-testid="profile-phone" className={fld} value={f.phone} onChange={set("phone")} /></div>
          <div><Label className={lbl}>{t("address")}</Label><Input data-testid="profile-address" className={fld} value={f.address} onChange={set("address")} /></div>
          <div><Label className={lbl}>{t("city")}</Label><Input data-testid="profile-city" className={fld} value={f.city} onChange={set("city")} /></div>
          <div>
            <Label className={lbl}>Sucursal</Label>
            <Input list="client-branches" data-testid="profile-branch" className={fld} value={f.branch} onChange={set("branch")} placeholder="Principal" />
            <datalist id="client-branches">{branches.map((b) => <option key={b} value={b} />)}</datalist>
          </div>
          <div className="sm:col-span-2"><Label className={lbl}>{t("occupation")}</Label><Input data-testid="profile-occupation" className={fld} value={f.occupation} onChange={set("occupation")} /></div>
          <div className="sm:col-span-2"><Label className={lbl}>{t("internal_notes")}</Label><Textarea data-testid="profile-notes" className="mt-1.5" rows={2} placeholder={t("internal_notes_ph")} value={f.notes} onChange={set("notes")} /></div>
        </div>
        <div className="flex justify-end mt-2">
          <Button data-testid="profile-save-btn" onClick={save} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900">{t("save_changes")}</Button>
        </div>

        {/* Change history */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="font-semibold text-emerald-900 flex items-center gap-2 mb-2 text-sm"><ClockCounterClockwise size={16} weight="duotone" /> {t("change_history")}</p>
          {history.length === 0 ? (
            <p className="text-slate-400 text-sm" data-testid="profile-history-empty">{t("no_changes_yet")}</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="profile-history-list">
              {history.map((h) => (
                <div key={h.id} className="rounded-lg border border-gray-100 px-3 py-2 text-xs">
                  <div className="flex justify-between text-slate-500 mb-1">
                    <span className="font-semibold text-emerald-800">{h.changed_by || "—"}</span>
                    <span>{(h.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <ul className="space-y-0.5">
                    {(h.changes || []).map((c, i) => (
                      <li key={i} className="text-slate-600">
                        <b>{t(PROFILE_FIELD_LABELS[c.field] || c.field)}</b>: <span className="line-through text-slate-400">{c.old || "—"}</span> → <span className="text-emerald-700">{c.new || "—"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BirthdaysPanel() {
  const { t, lang } = useI18n();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/birthdays?month=${month}`).then((r) => setMembers(r.data.members || [])).catch(() => setMembers([])).finally(() => setLoading(false));
  }, [month]);
  const MONTHS_ES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
  const monthName = (m) => lang === "es" ? MONTHS_ES[m - 1] : new Date(2020, m - 1, 1).toLocaleDateString("en-US", { month: "long" });
  const age = (dob) => { try { const y = parseInt(dob.split("-")[0]); return now.getFullYear() - y; } catch { return null; } };
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="birthdays-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2"><Cake size={22} weight="duotone" className="text-emerald-700" /><h3 className="font-display font-bold text-emerald-900 text-xl capitalize">{t("birthdays_of")} {monthName(month)}</h3></div>
        <select data-testid="birthday-month-select" value={month} onChange={(e) => setMonth(Number(e.target.value))} className="h-10 rounded-lg border border-emerald-200 px-3 text-sm text-emerald-900 capitalize bg-white">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
        </select>
      </div>
      {loading ? <p className="text-slate-500 py-4">{t("loading")}</p> : members.length === 0 ? (
        <p className="text-slate-400 py-6 text-center" data-testid="birthdays-empty">{t("no_birthdays")}</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="birthdays-list">
          {members.map((m) => {
            const initials = (m.name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
            const photo = m.photo_url ? `${process.env.REACT_APP_BACKEND_URL}${m.photo_url}` : null;
            const a = age(m.date_of_birth);
            return (
              <div key={m.id} className="flex items-center gap-3 rounded-xl border border-emerald-100 bg-emerald-50/40 p-3" data-testid={`birthday-${m.id}`}>
                {photo ? <img src={photo} alt={m.name} className="h-11 w-11 rounded-full object-cover" /> : <div className="h-11 w-11 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold">{initials}</div>}
                <div className="min-w-0">
                  <div className="font-semibold text-emerald-900 truncate">{m.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Cake size={12} weight="fill" className="text-emerald-600" /> {monthName(month)} {m.day}{a ? ` · ${t("turns_years")} ${a}` : ""}</div>
                  <div className="text-xs text-slate-400 truncate">{m.phone || m.email || ""}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function LinkUnlinkedDialog({ client, onDone }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ savings: [], term_deposits: [], loans: [] });
  const [sel, setSel] = useState({});
  const [history, setHistory] = useState([]);
  const load = () => {
    api.get("/admin/unlinked-accounts").then((r) => { setData(r.data); setSel({}); }).catch(() => {});
    api.get("/admin/link-history").then((r) => setHistory(r.data)).catch(() => {});
  };
  const autoLink = async () => {
    try { const { data } = await api.post("/admin/auto-link-suggested"); toast.success(`${t("link_matches")}: ${data.linked}`); load(); onDone(); }
    catch (e) { toast.error("Error"); }
  };
  const downloadReview = async () => {
    try {
      const res = await api.get("/admin/unlinked-accounts.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "cuentas_por_revisar.xlsx"; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line
  const link = async (base, id) => {
    try { await api.put(`${base}/${id}/link`, { user_id: client.user.id }); toast.success(t("saved")); load(); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const toggle = (id, base) => setSel((p) => { const n = { ...p }; if (n[id]) delete n[id]; else n[id] = base; return n; });
  const linkSelected = async () => {
    const entries = Object.entries(sel);
    if (entries.length === 0) return;
    try {
      for (const [id, base] of entries) { await api.put(`${base}/${id}/link`, { user_id: client.user.id }); }
      toast.success(t("saved")); load(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const Section = ({ title, items, base }) => items.length === 0 ? null : (
    <div>
      <p className="font-semibold text-emerald-900 text-sm mb-1.5">{title}</p>
      <div className="space-y-1.5">
        {items.map((a) => (
          <div key={a.id} className="flex items-center gap-2 rounded-lg border border-gray-100 px-3 py-2 text-sm" data-testid={`unlinked-${a.id}`}>
            <input type="checkbox" data-testid={`unlinked-check-${a.id}`} checked={!!sel[a.id]} onChange={() => toggle(a.id, base)} className="h-4 w-4 accent-emerald-700 shrink-0" />
            <div className="min-w-0 flex-1">
              <span className="font-mono-num text-emerald-800">{a.account_number}</span> <span className="text-slate-500">· {a.full_name || "—"} · {money(a.amount, a.currency)}</span>
              {a.needs_review ? <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700" data-testid={`review-${a.id}`}>⚠ {t("needs_review")}</span> : (a.suggested_name ? <span className="ml-2 text-[10px] text-emerald-600">{t("suggested")}: {a.suggested_name}</span> : null)}
            </div>
            <Button data-testid={`link-here-${a.id}`} size="sm" onClick={() => link(base, a.id)} className="bg-emerald-800 hover:bg-emerald-900 h-8 shrink-0">{t("link_here")}</Button>
          </div>
        ))}
      </div>
    </div>
  );
  const total = data.savings.length + data.term_deposits.length + data.loans.length;
  const selCount = Object.keys(sel).length;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`link-accounts-${client.user.id}`} size="sm" variant="outline" className="gap-1 h-8 px-2.5 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"><LinkSimple size={14} weight="bold" /> {t("link_accounts")}</Button>
      </DialogTrigger>
      <DialogContent data-testid="link-accounts-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900 flex items-center gap-2"><LinkSimple size={20} weight="duotone" /> {t("unlinked_accounts")}</DialogTitle>
          <DialogDescription>{client.user.name}</DialogDescription>
        </DialogHeader>
        {total === 0 ? <p className="text-slate-400 text-sm py-4" data-testid="no-unlinked">{t("no_unlinked")}</p> : (
          <div className="space-y-4">
            <Section title={t("savings_accounts")} items={data.savings} base="/admin/savings-accounts" />
            <Section title={t("term_deposits")} items={data.term_deposits} base="/admin/term-deposits" />
            <Section title={t("loan")} items={data.loans} base="/admin/loan-records" />
            <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-gray-100">
              <Button data-testid="export-review-btn" size="sm" variant="outline" onClick={downloadReview} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"><FileXls size={15} weight="bold" className="mr-1" /> Excel</Button>
              <Button data-testid="auto-link-btn" size="sm" variant="outline" onClick={autoLink} className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"><LinkSimple size={14} weight="bold" className="mr-1" /> {t("link_matches")}</Button>
              <Button data-testid="link-selected-btn" size="sm" disabled={selCount === 0} onClick={linkSelected} className="bg-emerald-800 hover:bg-emerald-900 disabled:opacity-40">{t("link_selected")}{selCount > 0 ? ` (${selCount})` : ""}</Button>
            </div>
          </div>
        )}
        {history.length > 0 && (
          <div className="pt-3 mt-1 border-t border-gray-100" data-testid="link-history">
            <p className="text-xs font-semibold text-slate-500 mb-1.5">{t("link_history_title")}</p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {history.slice(0, 8).map((h, i) => (
                <div key={i} className="text-[11px] text-slate-500"><b className="text-emerald-800">{h.actor_name}</b> · {h.detail} · {(h.created_at || "").slice(0, 16).replace("T", " ")}</div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ClientCard({ client, can, onDone }) {
  const { t } = useI18n();
  const u = client.user;
  const fileRef = useRef(null);
  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/admin/clients/${u.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(t("photo_updated")); await onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const sav = client.accounts.find((a) => a.type === "savings");
  const sha = client.accounts.find((a) => a.type === "shares");
  const loan = client.accounts.find((a) => a.type === "loan");
  const initials = (u.name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  const photo = u.photo_url ? `${process.env.REACT_APP_BACKEND_URL}${u.photo_url}` : null;
  const quickBtn = "gap-1 h-8 px-2.5 text-xs bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200";
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col" data-testid={`client-card-${u.id}`}>
      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          {photo ? (
            <img src={photo} alt={u.name} className="h-14 w-14 rounded-full object-cover border border-emerald-100" />
          ) : (
            <div className="h-14 w-14 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-lg" data-testid={`client-avatar-${u.id}`}>{initials || "?"}</div>
          )}
          {can("manage_accounts") && (
            <>
              <button type="button" data-testid={`client-photo-btn-${u.id}`} onClick={() => fileRef.current?.click()} className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-emerald-700 text-white flex items-center justify-center border-2 border-white hover:bg-emerald-800" title={t("upload_photo")}><UploadSimple size={12} weight="bold" /></button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid={`client-photo-input-${u.id}`} />
            </>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-emerald-900 truncate">{u.name}</div>
          <div className="text-xs text-slate-500 truncate">{u.email || "—"}</div>
          <div className="text-xs text-slate-500">{u.phone || "—"}</div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
        {u.national_id ? <span className="flex items-center gap-1"><IdentificationCard size={13} weight="duotone" />{u.national_id}</span> : null}
        {(u.address || u.city) ? <span className="flex items-center gap-1 truncate max-w-full">{[u.address, u.city].filter(Boolean).join(", ")}</span> : null}
        {u.created_at ? <span className="flex items-center gap-1"><CalendarCheck size={13} weight="duotone" />{t("member_since")} {(u.created_at || "").slice(0, 10)}</span> : null}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <AccBadge on={!!sav} icon={PiggyBank} label={t("savings")} value={sav ? money(sav.balance, sav.currency) : null} />
        <AccBadge on={!!sha} icon={ChartLineUp} label={t("shares")} value={sha ? money(sha.balance, sha.currency) : null} />
        <AccBadge on={!!loan} icon={HandCoins} label={t("loan")} value={loan ? money(loan.remaining_balance, loan.currency) : null} />
      </div>
      <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-2">
        {can("manage_accounts") && !sav && (
          <AddAccountDialog client={client} onDone={onDone} presetType="savings"
            trigger={<Button data-testid={`quick-add-savings-${u.id}`} size="sm" className={quickBtn}><Plus size={14} weight="bold" /> {t("savings")}</Button>} />
        )}
        {can("manage_accounts") && !sha && (
          <AddAccountDialog client={client} onDone={onDone} presetType="shares"
            trigger={<Button data-testid={`quick-add-shares-${u.id}`} size="sm" className={quickBtn}><Plus size={14} weight="bold" /> {t("shares")}</Button>} />
        )}
        {can("manage_loans") && !loan && (
          <AddAccountDialog client={client} onDone={onDone} presetType="loan"
            trigger={<Button data-testid={`quick-add-loan-${u.id}`} size="sm" className="gap-1 h-8 px-2.5 text-xs bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200"><Plus size={14} weight="bold" /> {t("loan")}</Button>} />
        )}
        {can("manage_accounts") && (
          <AddAccountDialog client={client} onDone={onDone} presetType="term_deposit"
            trigger={<Button data-testid={`quick-add-td-${u.id}`} size="sm" className="gap-1 h-8 px-2.5 text-xs bg-teal-50 text-teal-800 hover:bg-teal-100 border border-teal-200"><Plus size={14} weight="bold" /> {t("term_deposits")}</Button>} />
        )}
        {(can("manage_accounts") || can("manage_loans")) && (
          <AddAccountDialog client={client} onDone={onDone} can={can}
            trigger={<Button data-testid={`add-account-btn-${u.id}`} size="sm" variant="outline" className="gap-1 h-8 px-2.5 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50"><Plus size={14} weight="bold" /> {t("add_account")}</Button>} />
        )}
        {can("manage_accounts") && (
          <EditClientProfileDialog client={client} onDone={onDone}
            trigger={<Button data-testid={`edit-profile-${u.id}`} size="sm" variant="outline" className="gap-1 h-8 px-2.5 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50"><PencilSimple size={14} weight="bold" /> {t("edit_profile")}</Button>} />
        )}
        {can("manage_accounts") && (
          <LinkUnlinkedDialog client={client} onDone={onDone} />
        )}
        <ManageClientDialog client={client} onDone={onDone} can={can} />
      </div>
    </motion.div>
  );
}

export function LiquidateDialog({ deposit, onDone }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [pct, setPct] = useState("5");
  const [toSavings, setToSavings] = useState(!!deposit.user_id);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { api.get("/rates").then((r) => { if (r.data?.td_penalty_pct != null) setPct(String(r.data.td_penalty_pct)); }).catch(() => {}); } }, [open]);
  const principal = Number(deposit.principal || 0);
  const forfeit = Number(deposit.accrued_interest || 0);
  const penalty = Math.round(principal * (Number(pct) || 0)) / 100;
  const net = Math.round((principal - penalty) * 100) / 100;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/term-deposits/${deposit.id}/liquidate`, { penalty_pct: Number(pct) || 0, confirm: true, deposit_to_savings: toSavings });
      toast.success(t("td_liquidated_ok"));
      setOpen(false);
      await onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`liquidate-term-${deposit.id}`} size="sm" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" title={t("td_liquidate")}><Coins size={15} weight="bold" /></Button>
      </DialogTrigger>
      <DialogContent data-testid="liquidate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">{t("td_liquidate_title")} — {deposit.account_number}</DialogTitle>
          <DialogDescription className="sr-only">{t("td_liquidate_title")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-emerald-900 font-semibold">{t("td_penalty_pct")}</Label>
            <Input data-testid="liquidate-pct" type="number" step="0.5" className="mt-1.5 h-11 font-mono-num" value={pct} onChange={(e) => setPct(e.target.value)} />
          </div>
          <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm space-y-1.5 font-mono-num">
            <div className="flex justify-between"><span className="text-slate-600">{t("principal")}</span><b>{money(principal, deposit.currency)}</b></div>
            <div className="flex justify-between text-red-700"><span>{t("td_forfeit_interest")}</span><b>− {money(forfeit, deposit.currency)}</b></div>
            <div className="flex justify-between text-red-700"><span>{t("td_penalty_amount")} ({pct || 0}%)</span><b>− {money(penalty, deposit.currency)}</b></div>
            <div className="flex justify-between border-t border-orange-200 pt-1.5 text-emerald-800 text-base"><span>{t("td_net_payout")}</span><b data-testid="liquidate-net">{money(net, deposit.currency)}</b></div>
          </div>
          {deposit.user_id ? (
            <label className="flex items-center gap-2.5 cursor-pointer text-sm text-slate-700" data-testid="liquidate-to-savings-label">
              <input type="checkbox" data-testid="liquidate-to-savings" checked={toSavings} onChange={(e) => setToSavings(e.target.checked)} className="h-4 w-4 accent-emerald-700" />
              {t("td_deposit_to_savings")}
            </label>
          ) : (
            <p className="text-xs text-slate-400">{t("td_not_linked_cash")}</p>
          )}
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 leading-relaxed">{t("td_liquidate_reco")}</p>
        </div>
        <DialogFooter>
          <Button data-testid="liquidate-confirm" onClick={submit} disabled={busy} className="bg-orange-600 hover:bg-orange-700">{t("confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddAccountDialog({ client, onDone, presetType, trigger, can }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(presetType || "savings");
  const [currency, setCurrency] = useState("DOP");
  const today = new Date().toISOString().slice(0, 10);
  const nextYear = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().slice(0, 10);
  const [form, setForm] = useState({ principal: "", annual_rate: "12", term_months: "24", start_date: today, end_date: nextYear });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      const pt = presetType || "savings";
      setType(pt);
      setForm({ principal: "", annual_rate: pt === "term_deposit" ? "6" : "12", term_months: "24", start_date: today, end_date: nextYear });
      setCurrency("DOP");
    }
  }, [open, presetType]); // eslint-disable-line

  const submit = async () => {
    setBusy(true);
    try {
      if (type === "loan") {
        await api.post("/admin/loans", { user_id: client.user.id, principal: Number(form.principal), annual_rate: Number(form.annual_rate), term_months: Number(form.term_months), currency });
        toast.success(t("loan_created"));
      } else if (type === "term_deposit") {
        const tokens = (client.user.name || "Socio").trim().split(/\s+/);
        const first_name = tokens.length > 1 ? tokens.slice(0, -1).join(" ") : tokens[0];
        const last_name = tokens.length > 1 ? tokens.slice(-1)[0] : "";
        await api.post("/admin/term-deposits", {
          first_name, last_name, email: client.user.email || "", phone: client.user.phone || "",
          principal: Number(form.principal), currency, annual_rate: Number(form.annual_rate),
          start_date: form.start_date, end_date: form.end_date,
        });
        toast.success(t("term_deposit_created"));
      } else {
        await api.post("/admin/accounts", { user_id: client.user.id, type, currency });
        toast.success(t("account_opened"));
      }
      setOpen(false);
      await onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="add-account-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">{t("add_account")} — {client.user.name}</DialogTitle>
          <DialogDescription className="sr-only">{t("add_account")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!presetType && (
            <div>
              <Label className="text-emerald-900 font-semibold">{t("account_type")}</Label>
              <Select value={type} onValueChange={(v) => { setType(v); setForm((f) => ({ ...f, annual_rate: v === "term_deposit" ? "6" : v === "loan" ? "12" : f.annual_rate })); }}>
                <SelectTrigger data-testid="add-account-type" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(!can || can("manage_accounts")) && <SelectItem value="savings">{t("savings")}</SelectItem>}
                  {(!can || can("manage_accounts")) && <SelectItem value="shares">{t("shares")}</SelectItem>}
                  {(!can || can("manage_accounts")) && <SelectItem value="term_deposit">{t("term_deposits")}</SelectItem>}
                  {(!can || can("manage_loans")) && <SelectItem value="loan">{t("loan")}</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-emerald-900 font-semibold">{t("currency")}</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="add-account-currency" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="DOP">{t("dop")}</SelectItem>
                <SelectItem value="USD">{t("usd")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {type === "loan" && (
            <>
              <div>
                <Label className="text-emerald-900 font-semibold">{t("principal_amount")}</Label>
                <Input data-testid="add-loan-principal" type="number" className="mt-1.5 h-11 font-mono-num" value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-emerald-900 font-semibold">{t("rate")}</Label>
                  <Input data-testid="add-loan-rate" type="number" step="0.1" className="mt-1.5 h-11 font-mono-num" value={form.annual_rate} onChange={(e) => setForm({ ...form, annual_rate: e.target.value })} />
                </div>
                <div>
                  <Label className="text-emerald-900 font-semibold">{t("term")}</Label>
                  <Input data-testid="add-loan-term" type="number" className="mt-1.5 h-11 font-mono-num" value={form.term_months} onChange={(e) => setForm({ ...form, term_months: e.target.value })} />
                </div>
              </div>
            </>
          )}
          {type === "term_deposit" && (
            <>
              <div>
                <Label className="text-emerald-900 font-semibold">{t("deposit_amount")}</Label>
                <Input data-testid="add-td-principal" type="number" className="mt-1.5 h-11 font-mono-num" value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} />
              </div>
              <div>
                <Label className="text-emerald-900 font-semibold">{t("rate")}</Label>
                <Input data-testid="add-td-rate" type="number" step="0.1" className="mt-1.5 h-11 font-mono-num" value={form.annual_rate} onChange={(e) => setForm({ ...form, annual_rate: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-emerald-900 font-semibold">{t("start_date")}</Label>
                  <Input data-testid="add-td-start" type="date" className="mt-1.5 h-11" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-emerald-900 font-semibold">{t("end_date")}</Label>
                  <Input data-testid="add-td-end" type="date" className="mt-1.5 h-11" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="add-account-confirm" onClick={submit} disabled={busy || ((type === "loan" || type === "term_deposit") && !form.principal)} className="bg-emerald-800 hover:bg-emerald-900">{t("confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ManageClientDialog({ client, onDone, can }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [docs, setDocs] = useState([]);
  const [showAct, setShowAct] = useState(false);

  const refresh = async () => {
    const { data } = await api.get(`/admin/clients/${client.user.id}`);
    setDetail(data);
    try { const tr = await api.get(`/admin/clients/${client.user.id}/transfers`); setTransfers(tr.data); } catch (_) { setTransfers([]); }
    try { const dc = await api.get(`/admin/clients/${client.user.id}/id-documents`); setDocs(dc.data); } catch (_) { setDocs([]); }
  };

  useEffect(() => { if (open) refresh(); }, [open]);

  const savings = detail?.accounts.find((a) => a.type === "savings");
  const shares = detail?.accounts.find((a) => a.type === "shares");
  const hasLoan = detail?.accounts.some((a) => a.type === "loan");

  const openAccount = async (type, currency) => {
    try {
      await api.post("/admin/accounts", { user_id: client.user.id, type, currency });
      toast.success(t("account_opened"));
      await refresh(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const downloadTransferReceipt = async (tid, ref) => {
    try {
      const res = await api.get(`/admin/transfers/${tid}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `recibo_${ref}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`manage-btn-${client.user.id}`} size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">{t("view")}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manage-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">{t("manage_client")}: {client.user.name}</DialogTitle>
          <DialogDescription className="sr-only">{t("manage_client")}</DialogDescription>
        </DialogHeader>

        {!detail ? <p className="text-slate-500">{t("loading")}</p> : (
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-slate-500">{client.user.email || client.user.phone}</div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  data-testid={`admin-activities-${client.user.id}`}
                  size="sm" variant="outline"
                  className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"
                  onClick={() => setShowAct((v) => !v)}
                >
                  <ActIcon size={16} weight="bold" /> Actividades
                </Button>
                <Button
                  data-testid={`admin-statement-${client.user.id}`}
                  size="sm" variant="outline"
                  className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"
                  onClick={() => downloadStatement(`/admin/clients/${client.user.id}/statement`, `estado_${client.user.name}.pdf`).catch(() => toast.error("Error"))}
                >
                  <FilePdf size={16} weight="bold" /> {t("download_statement")}
                </Button>
              </div>
            </div>

            {showAct && (
              <div className="bg-slate-50 rounded-xl p-3" data-testid={`admin-activity-feed-${client.user.id}`}>
                <ActivityPanel baseUrl={`/admin/clients/${client.user.id}/activities`} emptyText="Este socio aún no tiene actividades." />
              </div>
            )}

            {/* Accounts */}
            <div className="grid sm:grid-cols-2 gap-4">
              <AccountBlock account={savings} type="savings" onOpen={openAccount} onTx={refresh} onDone={onDone} can={can} />
              <AccountBlock account={shares} type="shares" onOpen={openAccount} onTx={refresh} onDone={onDone} can={can} />
            </div>

            {/* Loan */}
            <div className="border-t border-gray-100 pt-4">
              {hasLoan ? (
                detail.accounts.filter((a) => a.type === "loan").map((l) => (
                  <div key={l.id} data-testid={`loan-block-${l.id}`} className="rounded-lg bg-amber-50 border border-amber-200 p-4 mb-3">
                    <p className="font-semibold text-amber-800 flex items-center gap-2"><HandCoins size={18} weight="duotone" /> {t("loan")}</p>
                    <div className="mt-2 text-sm flex flex-wrap gap-x-4 gap-y-1 font-mono-num">
                      <span>{t("principal_amount")}: <b>{money(l.principal, l.currency)}</b></span>
                      <span>{t("remaining_balance")}: <b className="text-amber-700">{money(l.remaining_balance, l.currency)}</b></span>
                      <span>{t("monthly_payment")}: <b>{money(l.monthly_payment, l.currency)}</b></span>
                      <span>{t("annual_rate")}: <b>{l.annual_rate}%</b></span>
                      <span>{t("payments_made")}: <b>{l.payments_made}</b></span>
                    </div>
                    {can("manage_loans") && l.remaining_balance > 0 && (
                      <LoanPayDialog loan={l} onDone={async () => { await refresh(); onDone(); }} />
                    )}
                    {l.remaining_balance <= 0 && (
                      <span className="inline-block mt-2 text-xs font-semibold bg-emerald-100 text-emerald-700 px-2 py-1 rounded">{t("loan_paid_off")}</span>
                    )}
                  </div>
                ))
              ) : can("manage_loans") ? (
                <CreateLoanDialog userId={client.user.id} onDone={async () => { await refresh(); onDone(); }} />
              ) : null}
            </div>

            {/* Transferencias */}
            {transfers.length > 0 && (
              <div className="border-t border-gray-100 pt-4" data-testid="admin-transfers-section">
                <p className="font-semibold text-emerald-900 flex items-center gap-2 mb-2"><PaperPlaneTilt size={18} weight="duotone" /> {t("transfers_tab")}</p>
                <div className="rounded-lg border border-gray-100 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-slate-400 border-b border-gray-100">
                      <th className="px-3 py-2 font-medium">{t("date")}</th>
                      <th className="px-3 py-2 font-medium">{t("counterpart")}</th>
                      <th className="px-3 py-2 font-medium text-right">{t("mv_amount")}</th>
                      <th className="px-3 py-2 font-medium text-right">{t("receipt")}</th>
                    </tr></thead>
                    <tbody>
                      {transfers.map((tr) => (
                        <tr key={tr.id} className="border-b border-gray-50 last:border-0" data-testid={`admin-transfer-row-${tr.id}`}>
                          <td className="px-3 py-2 text-slate-600">{(tr.created_at || "").slice(0, 10)}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded mr-2 ${tr.direction === "out" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{tr.direction === "out" ? t("transfer_out") : t("transfer_in")}</span>
                            <span className="text-emerald-900 font-semibold">{tr.counterpart_name}</span>
                          </td>
                          <td className={`px-3 py-2 text-right font-mono-num font-bold ${tr.direction === "out" ? "text-amber-700" : "text-emerald-700"}`}>{tr.direction === "out" ? "−" : "+"}{money(tr.amount, tr.currency)}</td>
                          <td className="px-3 py-2 text-right">
                            <Button data-testid={`admin-transfer-receipt-${tr.id}`} size="sm" variant="outline" onClick={() => downloadTransferReceipt(tr.id, tr.reference)} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1"><FilePdf size={13} weight="bold" /> {t("receipt")}</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Documentos vinculados */}
            {docs.length > 0 && (
              <div className="border-t border-gray-100 pt-4" data-testid="admin-client-docs-section">
                <p className="font-semibold text-emerald-900 flex items-center gap-2 mb-2"><IdentificationCard size={18} weight="duotone" /> {t("doc_client_docs")}</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {docs.map((d) => (
                    <a key={d.id} href={`${process.env.REACT_APP_BACKEND_URL}${d.image_url}`} target="_blank" rel="noreferrer" data-testid={`admin-client-doc-${d.id}`} className="block rounded-lg border border-gray-100 overflow-hidden hover:border-emerald-300">
                      {(d.image_url || "").toLowerCase().endsWith(".pdf") ? (
                        <div className="h-20 bg-slate-50 flex items-center justify-center text-slate-400 text-xs">PDF</div>
                      ) : (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${d.image_url}`} alt={d.person_name} className="h-20 w-full object-cover" />
                      )}
                      <p className="text-[10px] text-slate-500 px-1.5 py-1 truncate">{t(`doc_type_${d.doc_type}`)}{d.number ? ` · ${d.number}` : ""}</p>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AccountBlock({ account, type, onOpen, onTx, onDone, can }) {
  const { t } = useI18n();
  const [currency, setCurrency] = useState("DOP");
  const Icon = type === "savings" ? PiggyBank : ChartLineUp;
  return (
    <div data-testid={`account-block-${type}`} className="rounded-lg border border-gray-100 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={20} weight="duotone" className="text-emerald-700" />
        <span className="font-semibold text-emerald-900">{t(type)}</span>
      </div>
      {account ? (
        <>
          <p className="font-mono-num text-2xl font-bold text-emerald-900">{money(account.balance, account.currency)}</p>
          <p className="text-xs text-slate-400 mb-3">{t("accrued_earnings")}: <span className="font-mono-num text-emerald-600">{money(account.earnings, account.currency)}</span> · {account.rate}% · {account.currency === "USD" ? "USD" : "RD$"}</p>
          {can("manage_transactions") && (
            <>
              <TxDialog account={account} type="deposit" onDone={async () => { await onTx(); onDone(); }} />
              <TxDialog account={account} type="withdrawal" onDone={async () => { await onTx(); onDone(); }} />
            </>
          )}
        </>
      ) : can("manage_accounts") ? (
        <>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger data-testid={`open-${type}-currency`} className="h-9 mb-2 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="DOP">{t("dop")}</SelectItem>
              <SelectItem value="USD">{t("usd")}</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid={`open-${type}-btn`} onClick={() => onOpen(type, currency)} size="sm" className="w-full bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200 gap-1.5">
            <Plus size={16} weight="bold" /> {t("open_account")}
          </Button>
        </>
      ) : (
        <span className="text-xs text-slate-300">—</span>
      )}
    </div>
  );
}

export function TxDialog({ account, type, onDone }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/accounts/${account.id}/${type === "deposit" ? "deposit" : "withdraw"}`, { amount: Number(amount) });
      toast.success(t("tx_done"));
      setOpen(false); setAmount("");
      await onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`${type}-btn-${account.id}`} size="sm" variant="outline"
          className={`w-full mt-2 gap-1.5 ${type === "deposit" ? "border-emerald-300 text-emerald-800 hover:bg-emerald-50" : "border-red-200 text-red-700 hover:bg-red-50"}`}>
          <ArrowsLeftRight size={15} weight="bold" /> {t(type)}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="tx-dialog">
        <DialogHeader><DialogTitle className="font-display text-emerald-900">{t("register_movement")} — {t(type)}</DialogTitle><DialogDescription className="sr-only">movimiento</DialogDescription></DialogHeader>
        <div>
          <Label className="text-emerald-900 font-semibold">{t("amount_label")} ({account.currency === "USD" ? "USD" : "RD$"})</Label>
          <Input data-testid="tx-amount-input" type="number" className="mt-1.5 h-11 font-mono-num" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </div>
        <DialogFooter>
          <Button data-testid="tx-confirm-btn" onClick={submit} disabled={busy || !amount} className="bg-emerald-800 hover:bg-emerald-900">{t("confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LoanPayDialog({ loan, onDone }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(String(loan.monthly_payment || ""));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/loans/${loan.id}/pay`, { amount: Number(amount) });
      toast.success(t("payment_done"));
      setOpen(false);
      await onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`loan-pay-btn-${loan.id}`} size="sm" className="mt-3 bg-amber-600 hover:bg-amber-700 gap-1.5">
          <Coins size={16} weight="bold" /> {t("register_payment")}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="loan-pay-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">{t("register_payment")}</DialogTitle>
          <DialogDescription className="sr-only">{t("register_payment")}</DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-emerald-900 font-semibold">{t("payment_amount")} ({loan.currency === "USD" ? "USD" : "RD$"})</Label>
          <Input data-testid="loan-pay-amount" type="number" className="mt-1.5 h-11 font-mono-num" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
          <p className="text-xs text-slate-400 mt-1.5">{t("remaining_balance")}: {money(loan.remaining_balance, loan.currency)}</p>
        </div>
        <DialogFooter>
          <Button data-testid="loan-pay-confirm" onClick={submit} disabled={busy || !amount} className="bg-amber-600 hover:bg-amber-700">{t("confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CreateLoanDialog({ userId, onDone }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ principal: "", annual_rate: "12", term_months: "24", currency: "DOP" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/admin/loans", {
        user_id: userId,
        principal: Number(form.principal),
        annual_rate: Number(form.annual_rate),
        term_months: Number(form.term_months),
        currency: form.currency,
      });
      toast.success(t("loan_created"));
      setOpen(false);
      await onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-loan-btn" className="w-full bg-amber-600 hover:bg-amber-700 gap-1.5">
          <HandCoins size={18} weight="bold" /> {t("create_loan")}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="loan-dialog">
        <DialogHeader><DialogTitle className="font-display text-emerald-900">{t("create_loan")}</DialogTitle><DialogDescription className="sr-only">{t("create_loan")}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-emerald-900 font-semibold">{t("principal_amount")}</Label>
            <Input data-testid="loan-principal-input" type="number" className="mt-1.5 h-11 font-mono-num" value={form.principal} onChange={set("principal")} />
          </div>
          <div>
            <Label className="text-emerald-900 font-semibold">{t("currency")}</Label>
            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger data-testid="loan-currency-select" className="mt-1.5 h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DOP">{t("dop")}</SelectItem>
                <SelectItem value="USD">{t("usd")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-emerald-900 font-semibold">{t("rate")}</Label>
              <Input data-testid="loan-rate-input" type="number" step="0.1" className="mt-1.5 h-11 font-mono-num" value={form.annual_rate} onChange={set("annual_rate")} />
            </div>
            <div>
              <Label className="text-emerald-900 font-semibold">{t("term")}</Label>
              <Input data-testid="loan-term-input" type="number" className="mt-1.5 h-11 font-mono-num" value={form.term_months} onChange={set("term_months")} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="loan-confirm-btn" onClick={submit} disabled={busy || !form.principal} className="bg-amber-600 hover:bg-amber-700">{t("confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
